import { createClient } from '@supabase/supabase-js'
import { escapeHtml, generateStrongPassword, copyToClipboard, compressImage, maintenanceStatus, addDays } from '../utils.js'
import { initTelegramLink } from '../telegram-link.js'

// Initialize Supabase FIRST
const supabaseUrl = import.meta.env.VITE_SUPABASE_URL
const supabaseKey = import.meta.env.VITE_SUPABASE_ANON_KEY

// Session token is injected into every Supabase request so RLS policies
// can identify the authenticated tenant without Supabase Auth.
let sessionToken = sessionStorage.getItem('kixik_session_token') || null

const supabase = createClient(supabaseUrl, supabaseKey, {
  global: {
    fetch: (url, options = {}) => {
      if (sessionToken) {
        const headers = new Headers(options.headers)
        headers.set('x-session-token', sessionToken)
        options.headers = headers
      }
      return fetch(url, options)
    },
  },
})

// Import checklist functions AFTER
import {
  loadChecklists,
  openChecklistModal,
  closeChecklistModal,
  saveChecklist,
  deleteChecklist,
  loadChecklistsForDropdowns,
  loadChecklistForWorkLog,
  getCompletedChecklistItems
} from './checklist-functions.js'

// Import i18n system
import { t, setLanguage, updateTranslations, initI18n } from '../i18n.js'

// Buildings & zones (Hooldusraamat / EVS 807)
import {
  loadBuildings,
  initBuildings,
  locationBreadcrumbHtml,
  locationLabel,
  buildingNameFor,
  zoneNameFor,
  populateLocationSelects,
  readLocationSelects,
  buildingsCache,
  zonesCache,
} from './buildings.js'

// Service history (hoolduspäevik)
import { loadEquipmentServiceHistory } from './service-history.js'

// Hooldusraamat / EVS 807 maintenance book export
import { initHooldusraamat } from './hooldusraamat.js'

// Tags tab
import { loadTags, initTags } from './tags.js'



// Refresh Lucide icons after DOM changes
function refreshIcons() {
  if (typeof lucide !== 'undefined') {
    lucide.createIcons()
  }
}

/**
 * Coloured maintenance pill for an equipment row (EVS 807 hooldusstaatus).
 * Returns '' when the equipment has no maintenance plan at all.
 */
function maintenancePillHtml(equipment) {
  const { status, days } = maintenanceStatus(equipment.next_maintenance_date)
  if (status === 'none') return ''

  const label = {
    overdue: () => t('maintenance.overdueByDays').replace('{days}', Math.abs(days)),
    soon:    () => t('maintenance.dueInDays').replace('{days}', days),
    ok:      () => t('maintenance.onSchedule'),
  }[status]()

  const icon = { overdue: 'alert-triangle', soon: 'clock', ok: 'check-circle-2' }[status]
  return `<span class="maint-pill ${status}"><i data-lucide="${icon}"></i>${escapeHtml(label)}</span>`
}



// Export for checklist functions to use
export { supabase, currentUser, t }
export function showError(elementId, message) {
  const element = document.getElementById(elementId)
  if (element) {
    element.textContent = message
    element.classList.add('show')
  }
}
export function showSuccess(message) {
  alert('✅ ' + message)
}



// State
let currentUser = null
let tempPassword = null
let allEquipment = [] // Хранилище всего оборудования для фильтрации
let allSpecialists = [] // Кэш специалистов для фильтра
let billingReportData = [] // Данные последнего отчёта для экспорта
let equipmentFilters = {
  search: '',
  client: '',
  status: '',
  type: '',
  sort: '',
  specialist: '',
  noNfc: false,
  building: '',
  zone: ''
}

// DOM Elements
const loginScreen = document.getElementById('loginScreen')
const changePasswordScreen = document.getElementById('changePasswordScreen')
const dashboard = document.getElementById('dashboard')

const loginForm = document.getElementById('loginForm')
const loginError = document.getElementById('loginError')
const loginBtnText = document.getElementById('loginBtnText')

document.getElementById('forgotPasswordLink')?.addEventListener('click', (e) => {
  e.preventDefault()
  alert(t('auth.forgotPasswordHint') || 'To reset your password, please contact your KIXIK administrator.')
})

const changePasswordForm = document.getElementById('changePasswordForm')
const changePasswordError = document.getElementById('changePasswordError')
const changeBtnText = document.getElementById('changeBtnText')
const currentPasswordInput = document.getElementById('currentPassword')

const logoutBtn = document.getElementById('logoutBtn')
const userInfo = document.getElementById('userInfo')
const companyName = document.getElementById('companyName')

// Check if already logged in
checkAuth()

// Login form submit
loginForm.addEventListener('submit', async (e) => {
  e.preventDefault()
  
  const username = document.getElementById('username').value
  const password = document.getElementById('password').value
  
  await login(username, password)
})

// Change password form submit
changePasswordForm.addEventListener('submit', async (e) => {
  e.preventDefault()
  
  const newPassword = document.getElementById('newPassword').value
  const confirmPassword = document.getElementById('confirmPassword').value
  
  if (newPassword !== confirmPassword) {
    changePasswordError.textContent = t('error.passwordsDoNotMatch')
    changePasswordError.classList.add('show')
    return
  }
  
  if (newPassword.length < 8) {
    changePasswordError.textContent = t('error.passwordTooShort')
    changePasswordError.classList.add('show')
    return
  }
  
  await changePassword(newPassword)
})

// Logout
logoutBtn.addEventListener('click', logout)

// Functions
async function login(username, password) {
  loginBtnText.textContent = t('auth.loggingIn')
  loginForm.querySelector('button').disabled = true
  loginError.classList.remove('show')
  
  try {
    const { data, error } = await supabase.functions.invoke('auth-login', {
      body: { username, password, type: 'admin' }
    })

    if (error) throw new Error(error.message)
    if (data?.error) throw new Error(t('error.invalidCredentials'))

    const user = data.user

    // Store user + session token for RLS
    currentUser = user
    tempPassword = password
    sessionToken = data.sessionToken
    sessionStorage.setItem('kixik_user', JSON.stringify(user))
    sessionStorage.setItem('kixik_session_token', data.sessionToken)

    // Check if must change password
    if (user.must_change_password) {
      showChangePasswordScreen()
    } else {
      showDashboard()
    }
    
  } catch (error) {
    loginError.textContent = error.message
    loginError.classList.add('show')
  } finally {
    loginBtnText.textContent = t('auth.login')
    loginForm.querySelector('button').disabled = false
  }
}

async function changePassword(newPassword) {
  changeBtnText.textContent = t('auth.changing')
  changePasswordForm.querySelector('button').disabled = true
  changePasswordError.classList.remove('show')
  
  try {
    const { data, error } = await supabase.functions.invoke('auth-change-password', {
      body: {
        user_id: currentUser.id,
        new_password: newPassword,
        type: 'user',
        activate_tenant: true
      }
    })

    if (error) throw new Error(error.message)
    if (data?.error) throw new Error(data.error)

    // Update current user object
    currentUser.must_change_password = false
    if (currentUser.tenant) currentUser.tenant.status = 'active'
    sessionStorage.setItem('kixik_user', JSON.stringify(currentUser))
    
    alert(t('alert.passwordChanged'))
    
    showDashboard()
    
  } catch (error) {
    changePasswordError.textContent = t('error.prefix') + error.message
    changePasswordError.classList.add('show')
  } finally {
    changeBtnText.textContent = t('auth.change_password')
    changePasswordForm.querySelector('button').disabled = false
  }
}

function logout() {
  supabase.functions.invoke('auth-logout').catch(() => {}) // invalidate token server-side
  currentUser = null
  sessionToken = null
  sessionStorage.removeItem('kixik_user')
  sessionStorage.removeItem('kixik_session_token')
  
  loginScreen.style.display = 'flex'
  changePasswordScreen.style.display = 'none'
  dashboard.style.display = 'none'
  
  loginForm.reset()
  loginError.classList.remove('show')
}

function checkAuth() {
  const stored = sessionStorage.getItem('kixik_user')
  if (stored) {
    currentUser = JSON.parse(stored)
    
    if (currentUser.must_change_password) {
      showChangePasswordScreen()
    } else {
      showDashboard()
    }
  }
}

function showChangePasswordScreen() {
  loginScreen.style.display = 'none'
  changePasswordScreen.style.display = 'flex'
  dashboard.style.display = 'none'
  
  // Pre-fill current password
  currentPasswordInput.value = tempPassword || '••••••••'
}


// Equipment management
const addEquipmentBtn = document.getElementById('addEquipmentBtn')
const exportEquipmentBtn = document.getElementById('exportEquipmentBtn')
const addEquipmentModal = document.getElementById('addEquipmentModal')
const closeEquipmentModal = document.getElementById('closeEquipmentModal')
const cancelEquipmentBtn = document.getElementById('cancelBtn')
const addEquipmentForm = document.getElementById('addEquipmentForm')
const addEquipmentError = document.getElementById('addEquipmentError')
const addEquipmentBtnText = document.getElementById('addBtnText')

const step1 = document.getElementById('step1')
const step2 = document.getElementById('step2')
const backBtn = document.getElementById('backBtn')
const selectedTypeName = document.getElementById('selectedTypeName')
const dynamicFields = document.getElementById('dynamicFields')
const nfcTagSelect = document.getElementById('nfcTag')
const clientCompaniesDatalist = document.getElementById('clientCompanies')

let selectedEquipmentType = null
let equipmentTemplates = []

// Show only the equipment type "modules" enabled for this tenant.
// Existing equipment of a disabled type stays fully visible elsewhere —
// this only gates which types can be picked when adding NEW equipment.
function applyEquipmentModuleVisibility() {
  const enabled = currentUser?.tenant?.enabled_equipment_types
  if (!enabled) return // column missing/null — show everything (fail open)
  document.querySelectorAll('#step1 .type-card[data-type]').forEach(card => {
    card.style.display = enabled.includes(card.dataset.type) ? '' : 'none'
  })
}

/// Open add equipment modal
addEquipmentBtn?.addEventListener('click', () => {
  addEquipmentModal.style.display = 'flex'
  step1.style.display = 'block'
  step2.style.display = 'none'
  addEquipmentForm.reset()
  selectedEquipmentType = null
  loadAvailableTags()
  loadClientCompanies()
  applyEquipmentModuleVisibility()
  populateLocationSelects('addBuildingSelect', 'addZoneSelect')
})
// Export equipment to Excel
exportEquipmentBtn?.addEventListener('click', async () => {
  try {
    // Show loading state
    exportEquipmentBtn.textContent = t('equipment.exporting')
    exportEquipmentBtn.disabled = true
    
    // Используем уже отфильтрованный список
    const filtered = getFilteredEquipment()
    if (!filtered || filtered.length === 0) {
      alert(t('equipment.exportNoData'))
      return
    }
    await exportEquipmentToExcel(filtered)
    
    // Show success message
    showSuccess(t('equipment.exportSuccess'))
    
  } catch (error) {
    console.error('Export error:', error)
    alert(t('error.exportFailed') + ': ' + error.message)
  } finally {
    exportEquipmentBtn.textContent = t('equipment.exportBtn')
    exportEquipmentBtn.disabled = false
  }
})

/**
 * Export equipment data to Excel (.xlsx)
 */
async function exportEquipmentToExcel(equipment) {
  const workbook = new ExcelJS.Workbook()
  const worksheet = workbook.addWorksheet(t('equipment.sheetName'))

  // Собираем все уникальные поля из custom_fields по всему списку
  const baseKeys = ['name', 'client_company', 'serial_number', 'location', 'status', 'nfc_id']
  const legacyKeys = ['cpu', 'ram', 'disk', 'os', 'user', 'ip', 'subtype']
  const customKeys = []
  equipment.forEach(item => {
    Object.keys(item.custom_fields || {}).forEach(key => {
      if (!customKeys.includes(key) && !baseKeys.includes(key) && !legacyKeys.includes(key)) {
        customKeys.push(key)
      }
    })
  })

  // Базовые колонки
  const columns = [
    { header: 'Name', key: 'name', width: 25 },
    { header: 'Type', key: 'type', width: 20 },
    { header: 'Client Company', key: 'client', width: 25 },
    { header: 'Serial Number', key: 'serial', width: 18 },
    { header: 'Building', key: 'building', width: 22 },
    { header: 'Zone', key: 'zone', width: 22 },
    { header: 'Location', key: 'location', width: 20 },
    { header: 'Price (€)', key: 'price', width: 14 },
    { header: 'Status', key: 'status', width: 12 },
    { header: 'NFC Tag', key: 'nfc', width: 20 },
    { header: 'Last Maintenance', key: 'lastMaint', width: 18 },
    { header: 'Next Maintenance', key: 'nextMaint', width: 18 },
    { header: 'Maintenance Interval (days)', key: 'maintInterval', width: 24 },
    { header: 'Created At', key: 'created', width: 18 },
  ]

  // Добавляем динамические колонки из custom_fields
  customKeys.forEach(key => {
    columns.push({ header: key, key: `cf_${key}`, width: 20 })
  })

  worksheet.columns = columns

  // Строки данных
  equipment.forEach(item => {
    const row = {
      name: item.name || '-',
      type: item.equipment_type?.name || '-',
      client: item.client_company || '-',
      serial: item.serial_number || '-',
      building: buildingNameFor(item),
      zone: zoneNameFor(item),
      location: item.location || '-',
      price: item.price != null ? item.price : '-',
      status: item.status || '-',
      nfc: item.nfc_id || '-',
      lastMaint: item.last_maintenance_date ? new Date(item.last_maintenance_date).toLocaleDateString() : '-',
      nextMaint: item.next_maintenance_date ? new Date(item.next_maintenance_date).toLocaleDateString() : '-',
      maintInterval: item.maintenance_interval_days ?? '-',
      created: new Date(item.created_at).toLocaleString()
    }
    // Добавляем кастомные поля
    customKeys.forEach(key => {
      row[`cf_${key}`] = item.custom_fields?.[key] || '-'
    })
    worksheet.addRow(row)
  })

  // Стили шапки
  // Автоширина колонок
  worksheet.columns.forEach(col => {
    let maxLen = col.header ? col.header.length : 10
    col.eachCell({ includeEmpty: false }, cell => {
      const val = cell.value ? String(cell.value) : ''
      if (val.length > maxLen) maxLen = val.length
    })
    col.width = Math.min(maxLen + 2, 50)
  })	
  worksheet.getRow(1).font = { bold: true }
  worksheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7E6E6' } }
  worksheet.views = [{ state: 'frozen', ySplit: 1 }]
  worksheet.autoFilter = {
    from: { row: 1, column: 1 },
    to: { row: 1, column: worksheet.columns.length }
  }

  const date = new Date().toISOString().substring(0, 10)
  const types = [...new Set(equipment.map(i => i.equipment_type?.name).filter(Boolean))]
  const clients = [...new Set(equipment.map(i => i.client_company).filter(Boolean))]
  const typePart = types.length === 1 ? types[0].replace(/\s+/g, '_') : 'mixed'
  const clientPart = clients.length === 1 ? clients[0].replace(/\s+/g, '_') : ''
  const filename = [date, typePart, clientPart].filter(Boolean).join('_') + '.xlsx'

  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
}
// Close modal
function closeEquipmentModalFn() {
  addEquipmentModal.style.display = 'none'
}

closeEquipmentModal?.addEventListener('click', closeEquipmentModalFn)
cancelEquipmentBtn?.addEventListener('click', closeEquipmentModalFn)
addEquipmentModal?.querySelector('.modal-overlay')?.addEventListener('click', closeEquipmentModalFn)

// Equipment type selection
document.querySelectorAll('.type-card').forEach(card => {
  card.addEventListener('click', async () => {
    const type = card.dataset.type
    
    // Highlight selected
    document.querySelectorAll('.type-card').forEach(c => c.classList.remove('selected'))
    card.classList.add('selected')
    
    // Load template
    await loadEquipmentTemplate(type)
  })
})

// Back button
backBtn?.addEventListener('click', () => {
  step2.style.display = 'none'
  step1.style.display = 'block'
})

// Load equipment template (ПОЛНОСТЬЮ ИСПРАВЛЕННАЯ)
// 1. Обновляем иконки
function getLucideIcon(typeCode) {
  const iconMap = {
    'it_office': 'laptop',
    'industrial': 'factory',
    'vehicles': 'truck',
    'safety': 'shield-alert',
    'small_tools': 'wrench'
  }
  return iconMap[typeCode] || 'package'
}

// 2. Загрузка шаблона (Делим на 2 блока)
async function loadEquipmentTemplate(typeCode) {
  try {
    const { data: template, error } = await supabase
      .from('equipment_types')
      .select('*')
      .eq('code', typeCode)
      .single()
    
    if (error) throw error
    
    selectedEquipmentType = template
    const templateLucideIcon = getLucideIcon(template.code)
    selectedTypeName.innerHTML = `<i data-lucide="${templateLucideIcon}"></i> ${template.name}`
    
    // Очищаем контейнеры (кроме статичных полей, которые мы оставили в HTML)
    // Но лучше просто добавлять динамические поля В КОНЕЦ контейнеров
    
    // Passport Container
    const passportContainer = document.getElementById('passportFields');
    // Очищаем всё КРОМЕ Name и Client (они первые 2 элемента)
    // Самый простой способ: удалить добавленные классом dynamic-field
    document.querySelectorAll('.dynamic-field').forEach(e => e.remove());

    // Maintenance Container
    const maintenanceContainer = document.getElementById('maintenanceFields');
    // Удаляем динамические
    maintenanceContainer.querySelectorAll('.dynamic-field').forEach(e => e.remove());

    const schema = template.fields_schema;
    
    if (schema) {
      // Рендерим Passport Fields
      if (schema.passport) {
        schema.passport.forEach(field => {
          // Пропускаем поля, которые уже есть жестко в HTML (name, client_company, serial, location - location в maintenance)
          if (['name', 'client_company'].includes(field.id)) return; 
          
          const html = generateFieldHTML(field);
          // Вставляем как DOM элемент
          const div = document.createElement('div');
          div.className = 'dynamic-field';
          div.innerHTML = html;
          passportContainer.appendChild(div);
        });
      }

      // Рендерим Maintenance Fields
      if (schema.maintenance) {
        schema.maintenance.forEach(field => {
           // Пропускаем жесткие поля
           if (['location', 'checklist_id', 'nfc_id'].includes(field.id)) return;

           const html = generateFieldHTML(field);
           const div = document.createElement('div');
           div.className = 'dynamic-field';
           div.innerHTML = html;
           // Вставляем ПЕРЕД NFC и Checklist, или просто в начало контейнера?
           // Давайте вставим в начало контейнера maintenance, до Location
           maintenanceContainer.insertBefore(div, maintenanceContainer.firstChild);
        });
      }
    }
    
    step1.style.display = 'none'
    step2.style.display = 'block'
    refreshIcons()
    
  } catch (error) {
    console.error('Error loading template:', error)
    alert('Error loading equipment template')
  }
}

// 3. Сохранение формы (Сбор данных из обоих блоков)
// Generate field HTML based on field schema
function generateFieldHTML(field) {
  const required = field.required ? '*' : ''
  const requiredAttr = ''
  
  let inputHTML = ''
  
  switch (field.type) {
    case 'text':
      inputHTML = `
        <input 
          type="text" 
          id="field_${field.id}" 
          ${requiredAttr}
        >
      `
      break
    
    case 'number':
      inputHTML = `
        <input 
          type="number" 
          id="field_${field.id}" 
          ${requiredAttr}
        >
      `
      break
    
    case 'select':
      const options = field.options.map(opt => `<option value="${opt}">${opt}</option>`).join('')
      inputHTML = `
        <select id="field_${field.id}" ${requiredAttr}>
          <option value="">Select...</option>
          ${options}
        </select>
      `
      break
    
    case 'date':
      inputHTML = `
        <input 
          type="date" 
          id="field_${field.id}" 
          ${requiredAttr}
        >
      `
      break
    
    default:
      inputHTML = `
        <input 
          type="text" 
          id="field_${field.id}" 
          ${requiredAttr}
        >
      `
  }
  
  return `
    <div class="form-group">
      <label for="field_${field.id}">${t('equipment.' + field.id) || field.label} ${required}</label>
      ${inputHTML}
    </div>
  `
}

// Load available NFC tags
async function loadAvailableTags() {
  try {
    const { data: tags, error } = await supabase
      .from('nfc_tags')
      .select('*')
      .eq('tenant_id', currentUser.tenant_id)
      .eq('is_assigned', false)
      .order('nfc_id', { ascending: true })
    
    if (error) throw error
    
    nfcTagSelect.innerHTML = '<option value="">Select Smart Tag...</option>'
    
    tags.forEach(tag => {
      const option = document.createElement('option')
      option.value = tag.nfc_id
      option.textContent = tag.nfc_id
      nfcTagSelect.appendChild(option)
    })
    
    loadChecklistsForDropdowns()
	loadSpecialistsForDropdown()  
    
  } catch (error) {
    console.error('Error loading tags:', error)
  }
}
async function loadSpecialistsForDropdown(selectedId = null) {
  try {
    const { data: specialists, error } = await supabase
      .from('users')
      .select('id, full_name, email, phone')
      .eq('tenant_id', currentUser.tenant_id)
      .eq('role', 'mechanic')
      .eq('is_active', true)
      .order('full_name', { ascending: true })

    if (error) throw error

    const dropdowns = [
      document.getElementById('assignedSpecialistSelect'),
      document.getElementById('editAssignedSpecialistSelect')
    ]

    dropdowns.forEach(dropdown => {
      if (!dropdown) return

      dropdown.innerHTML = '<option value="">— No specialist assigned —</option>'

      specialists?.forEach(s => {
        const option = document.createElement('option')
        option.value = s.id
        option.textContent = s.full_name
        option.dataset.email = s.email || ''
        option.dataset.phone = s.phone || ''
        if (selectedId && s.id === selectedId) option.selected = true
        dropdown.appendChild(option)
      })
    })

  } catch (error) {
    console.error('Error loading specialists for dropdown:', error)
  }
}

// Load existing client companies for autocomplete
async function loadClientCompanies() {
  try {
    const { data: equipment, error } = await supabase
      .from('equipment')
      .select('client_company')
      .eq('tenant_id', currentUser.tenant_id)
      .not('client_company', 'is', null)
    
    if (error) throw error
    
    // Get unique companies
    const companies = [...new Set(equipment.map(e => e.client_company).filter(Boolean))]
    
    clientCompaniesDatalist.innerHTML = ''
    companies.forEach(company => {
      const option = document.createElement('option')
      option.value = company
      clientCompaniesDatalist.appendChild(option)
    })
    
  } catch (error) {
    console.error('Error loading companies:', error)
  }
}




// Submit equipment form
addEquipmentForm?.addEventListener('submit', async (e) => {
  e.preventDefault()
  
  addEquipmentBtnText.textContent = 'Adding...'
  addEquipmentForm.querySelector('button[type="submit"]').disabled = true
  addEquipmentError.classList.remove('show')
  
  try {
    const gV = (id) => document.getElementById(id)?.value || null;

    // 2. Собираем базовые данные
    const name = gV('name');
    const clientCompany = gV('clientCompany');
    const location = gV('location');
    const priceRaw = document.getElementById('equipmentPrice')?.value
    const price = priceRaw !== '' && priceRaw !== null && priceRaw !== undefined ? parseFloat(priceRaw) : null
    const checklistId = gV('checklistSelect');
    const nfcId = nfcTagSelect?.value || null;

    // 4. Собираем кастомные поля из динамических полей формы (по схеме)
    const customFields = {};
    const schema = selectedEquipmentType?.fields_schema;
    const baseFields = ['name', 'client_company', 'location', 'checklist_id', 'nfc_id'];

    const collectFromSchema = (fieldList) => {
      if (!fieldList) return;
      fieldList.forEach(field => {
        if (baseFields.includes(field.id)) return;
        const input = document.getElementById(`field_${field.id}`);
        if (input) customFields[field.id] = input.value || null;
      });
    };

    collectFromSchema(schema?.passport);
    collectFromSchema(schema?.maintenance);

    

    if (!selectedEquipmentType?.id) {
      throw new Error('Equipment type is not selected!');
    }

    // Building / zone (Hooldusraamat hierarchy) — both optional
    const addLocation = readLocationSelects('addBuildingSelect', 'addZoneSelect')
    const addInterval = parseInt(document.getElementById('addMaintenanceInterval')?.value, 10)

    // 4. Запись в Supabase
    const { data: equipment, error: equipmentError } = await supabase
      .from('equipment')
      .insert([{
        tenant_id: currentUser.tenant_id,
        equipment_type_id: selectedEquipmentType.id,
        name: name,
        client_company: clientCompany,
        serial_number: customFields.serial_number || null,
        location: location,
        building_id: addLocation.building_id,
        zone_id: addLocation.zone_id,
        maintenance_interval_days: Number.isFinite(addInterval) && addInterval > 0 ? addInterval : null,
        price: price,
        status: customFields.status || 'active',
        custom_fields: customFields,
		ai_scan_data: document.getElementById('add_ai_scan_data')?.value || null,
        nfc_id: nfcId,
        last_maintenance_date: customFields.last_inspection_date || customFields.last_maintenance_date || null,
        next_maintenance_date: customFields.next_inspection_date || customFields.inspection_deadline || customFields.next_maintenance_date || null,
        checklist_id: checklistId,
		assigned_specialist_id: document.getElementById('assignedSpecialistSelect')?.value || null,  
        created_by_user_id: currentUser.id
      }])
      .select()
      .single();

    if (equipmentError) throw equipmentError;
	// Логика фото для создания
    // Логика фото для создания
    const photoFile = document.getElementById('equipmentPhotoInput')?.files[0]
    if (photoFile) {
      console.log('Найдено фото для загрузки:', photoFile.name);
      try {
        // У тебя в коде переменная называется addEquipmentBtnText (строка 524)
        addEquipmentBtnText.textContent = 'Uploading photo...';
        
        const photoUrl = await uploadPhoto(photoFile, equipment.id);
        console.log('Фото загружено, URL:', photoUrl);

        if (photoUrl) {
          const { error: photoUpdateError } = await supabase
            .from('equipment')
            .update({ photo_url: photoUrl })
            .eq('id', equipment.id);
          
          if (photoUpdateError) throw photoUpdateError;
        }
      } catch (uploadErr) {
        console.error('КРИТИЧЕСКАЯ ОШИБКА ФОТО:', uploadErr);
        alert('Photo upload failed: ' + uploadErr.message); // Чтобы ты точно увидел ошибку
      }
    }

    // 5. Обновление NFC тега
    if (nfcId) {
      const { error: tagError } = await supabase
        .from('nfc_tags')
        .update({
          equipment_id: equipment.id,
          is_assigned: true,
          assigned_at: new Date().toISOString()
        })
        .eq('nfc_id', nfcId);
      
      if (tagError) throw tagError;
    }

    // 6. ТВОЙ ФИНАЛ (уведомление и обновление интерфейса)
    alert(`✅ Equipment "${name}" added successfully!`);
    
    if (typeof closeEquipmentModalFn === 'function') {
      closeEquipmentModalFn();
    }
    
    await loadEquipmentList();
    await loadStats();
    
  } catch (error) {
    console.error('Error adding equipment:', error);
    addEquipmentError.textContent = `Error: ${error.message}`;
    addEquipmentError.classList.add('show');
  } finally {
    addEquipmentBtnText.textContent = 'Add Equipment';
    const submitBtn = addEquipmentForm.querySelector('button[type="submit"]');
    if (submitBtn) submitBtn.disabled = false;
  }
});
// Load equipment list
async function loadEquipmentList() {
  const container = document.getElementById('equipmentList')
  
  try {
    const { data: equipment, error } = await supabase
      .from('equipment')
      .select(`
        *,
        equipment_type:equipment_types (
          name,
          icon,
          code
        ),
        checklist:checklists!equipment_checklist_id_fkey (
          id,
          name
        )
      `)
      .eq('tenant_id', currentUser.tenant_id)
      .is('deleted_at', null)
      .order('created_at', { ascending: false })
    
    if (error) throw error
    
    // Сохраняем все оборудование для фильтрации
    allEquipment = equipment || []
    
    // Загружаем список клиентов и специалистов в фильтр
    loadClientFilter()
    loadSpecialistFilter()

    // Применяем фильтры и рендерим
    renderFilteredEquipment()
    
  } catch (error) {
    console.error('Error loading equipment:', error)
    container.innerHTML = '<p class="loading">Error loading equipment</p>'
  }
}

// Load dashboard stats
// Load dashboard stats
async function loadStats() {
  try {
    // Total equipment
    const { count: equipmentCount } = await supabase
      .from('equipment')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', currentUser.tenant_id)
      .is('deleted_at', null)
    
    document.getElementById('totalEquipment').textContent = equipmentCount || 0
    
    // Available tags
    const { count: tagsCount } = await supabase
      .from('nfc_tags')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', currentUser.tenant_id)
      .eq('is_assigned', false)
    
    document.getElementById('availableTags').textContent = tagsCount || 0
    
    // Total specialists
    const { count: specialistsCount } = await supabase
      .from('users')
      .select('*', { count: 'exact', head: true })
      .eq('tenant_id', currentUser.tenant_id)
      .eq('role', 'mechanic')
      .eq('is_active', true)
    
    document.getElementById('totalSpecialists').textContent = specialistsCount || 0
    
  } catch (error) {
    console.error('Error loading stats:', error)
  }
}



// Update showDashboard function to load equipment
function showDashboard() {
  loginScreen.style.display = 'none'
  changePasswordScreen.style.display = 'none'
  dashboard.style.display = 'block'
  document.body.classList.add('with-sidebar')

  // Show user info
  userInfo.textContent = currentUser.full_name
  companyName.textContent = currentUser.tenant.company_name

  initTelegramLink(supabase)

  // Load data
  loadStats()
  loadBuildings()
  loadEquipmentList()
  loadMechanicsList()
  loadChecklists()
  loadOverdueCount()
  loadServiceRequestsCount()
}



// Equipment Detail Modal
const equipmentDetailModal = document.getElementById('equipmentDetailModal')
const closeDetailModal = document.getElementById('closeDetailModal')
const closeDetailBtn = document.getElementById('closeDetailBtn')
const deleteEquipmentBtn = document.getElementById('deleteEquipmentBtn');
const editEquipmentBtn = document.getElementById('editEquipmentBtn')
const addWorkLogBtn = document.getElementById('addWorkLogBtn')

const editEquipmentModal = document.getElementById('editEquipmentModal')
const closeEditModal = document.getElementById('closeEditModal')
const cancelEditBtn = document.getElementById('cancelEditBtn')
const editEquipmentForm = document.getElementById('editEquipmentForm')
const editEquipmentError = document.getElementById('editEquipmentError')
const editBtnText = document.getElementById('editBtnText')

let currentEquipment = null

// Close detail modal
function closeDetailModalFn() {
  equipmentDetailModal.style.display = 'none'
  currentEquipment = null
}

closeDetailModal?.addEventListener('click', closeDetailModalFn)
closeDetailBtn?.addEventListener('click', closeDetailModalFn)
equipmentDetailModal?.querySelector('.modal-overlay')?.addEventListener('click', closeDetailModalFn)

// Close edit modal
function closeEditModalFn() {
  editEquipmentModal.style.display = 'none'
}

closeEditModal?.addEventListener('click', closeEditModalFn)
cancelEditBtn?.addEventListener('click', closeEditModalFn)
editEquipmentModal?.querySelector('.modal-overlay')?.addEventListener('click', closeEditModalFn)

async function openEquipmentDetail(id) {
  try {
    const { data: equipment, error } = await supabase
      .from('equipment')
      .select(`
        *,
        equipment_type:equipment_types (
          id,
          code,
          name,
          icon,
          fields_schema
        ),
        nfc_tag:nfc_tags!equipment_nfc_id_fkey (
          nfc_id,
          nfc_url,
          url_code
        )
      `)
      .eq('id', id)
      .single()
    
    if (error) throw error
    
    currentEquipment = equipment
    
    document.getElementById('detailEquipmentName').textContent = equipment.name
    const detailLucideIcon = getLucideIcon(equipment.equipment_type.code)
    document.getElementById('detailEquipmentType').innerHTML = 
      `<i data-lucide="${detailLucideIcon}"></i> ${equipment.equipment_type.name}`
    
    renderDetailItem('detailClientCompany', t('equipment.clientCompanyLabel'), equipment.client_company)
    renderDetailItem('detailSerialNumber', t('equipment.serialLabel'), equipment.serial_number)
    renderDetailItem('detailLocation', t('equipment.locationLabel'), equipment.location)
    renderDetailItem('detailPrice', t('equipment.priceLabel'), equipment.price != null ? equipment.price + ' €' : null)
    
    const statusBadge = equipment.status === 'active' ? 'status-active' :
      equipment.status === 'repair' ? 'status-repair' : 'status-retired'
    document.getElementById('detailStatus').innerHTML = `
      <div class="detail-label">${t('equipment.statusLabel')}</div>
      <div class="detail-value">
      <span class="status-badge ${statusBadge}">${equipment.status}</span>
      </div>
    `

    if (equipment.nfc_tag) {
      document.getElementById('detailNfcTag').innerHTML = `
        <div class="detail-label">${t('equipment.nfcTagLabel')}</div>
        <div class="detail-value">
        <span class="nfc-tag-display">${equipment.nfc_tag.nfc_id}</span>
        </div>
      `
    } else {
      renderDetailItem('detailNfcTag', t('equipment.nfcTagLabel'), null)
    }

    const createdDate = new Date(equipment.created_at).toLocaleDateString()
    renderDetailItem('detailCreated', t('equipment.created'), createdDate)

    if (equipment.checklist_id) {
      const { data: checklist } = await supabase
        .from('checklists')
        .select('name')
        .eq('id', equipment.checklist_id)
        .single()
      renderDetailItem('detailChecklist', t('equipment.checklistLabel'), checklist?.name || equipment.checklist_id)
    } else {
      renderDetailItem('detailChecklist', t('equipment.checklistLabel'), null)
    }
    
    // Fields tracked automatically by work logs — shown in the maintenance block below, not in custom fields
    const maintDateFieldIds = ['last_maintenance_date', 'next_maintenance_date', 'last_inspection_date', 'next_inspection_date', 'inspection_deadline']

    if (equipment.custom_fields && Object.keys(equipment.custom_fields).length > 0) {
      const customFieldsSection = document.getElementById('customFieldsSection')
      const customFieldsContainer = document.getElementById('detailCustomFields')
      customFieldsSection.style.display = 'block'
      customFieldsContainer.innerHTML = ''
      const fieldsSchema = equipment.equipment_type.fields_schema?.fields || []
      let hasVisible = false
      Object.entries(equipment.custom_fields).forEach(([key, value]) => {
        if (maintDateFieldIds.includes(key)) return
        const isPlaceholder = value?.includes?.('e.g.,') || value?.includes?.('dd/mm/yyyy') || value?.includes?.('YYYY-MM-DD')
        if (isPlaceholder) return
        if (!value) return
        hasVisible = true
        const fieldDef = fieldsSchema.find(f => f.id === key)
        const label = t(`equipment.${key}`) || fieldDef?.label || key
        const div = document.createElement('div')
        div.className = 'detail-item'
        div.innerHTML = `
          <div class="detail-label">${label}</div>
          <div class="detail-value">${value}</div>
        `
        customFieldsContainer.appendChild(div)
      })
      if (!hasVisible) customFieldsSection.style.display = 'none'
    } else {
      document.getElementById('customFieldsSection').style.display = 'none'
    }

    // Maintenance dates — always from equipment table columns (managed by work logs)
    const detailMaintSection = document.getElementById('detailMaintenanceDates')
    if (detailMaintSection) {
      const lastM = equipment.last_maintenance_date
        ? new Date(equipment.last_maintenance_date).toLocaleDateString()
        : '—'
      const nextM = equipment.next_maintenance_date
        ? new Date(equipment.next_maintenance_date).toLocaleDateString()
        : '—'
      const isOverdue = equipment.next_maintenance_date && new Date(equipment.next_maintenance_date) < new Date().setHours(0, 0, 0, 0)

      const engineHoursTypes = ['industrial', 'vehicles']
      const showEngineHours = engineHoursTypes.includes(equipment.equipment_type?.code)
      const engineHoursRow = showEngineHours ? `
          <div class="detail-item">
            <div class="detail-label">${t('equipment.engine_hours')}</div>
            <div class="detail-value">${equipment.engine_hours != null ? equipment.engine_hours + ' h' : '—'}</div>
          </div>` : ''

      const intervalRow = equipment.maintenance_interval_days
        ? `
          <div class="detail-item">
            <div class="detail-label">${t('maintenance.intervalLabel')}</div>
            <div class="detail-value">${equipment.maintenance_interval_days} ${t('maintenance.daysShort')}</div>
          </div>` : ''

      const locationLabelText = locationLabel(equipment)
      const locationRow = locationLabelText
        ? `
          <div class="detail-item">
            <div class="detail-label">${t('buildings.assignTitle')}</div>
            <div class="detail-value">${escapeHtml(locationLabelText)}</div>
          </div>` : ''

      detailMaintSection.style.display = 'block'
      detailMaintSection.innerHTML = `
        <h3>${t('equipment.maintenanceSectionTitle') || t('equipment.maintenanceDate')}</h3>
        <div class="detail-grid">
          ${locationRow}
          <div class="detail-item">
            <div class="detail-label">${t('equipment.maintenanceDate')}</div>
            <div class="detail-value">${lastM}</div>
          </div>
          <div class="detail-item">
            <div class="detail-label">${t('equipment.nextMaintenanceDate')}</div>
            <div class="detail-value${isOverdue ? ' date-overdue' : ''}">${nextM}</div>
          </div>
          ${intervalRow}
          ${engineHoursRow}
        </div>
        <div style="margin-top:10px;">
          <button class="btn btn-secondary btn-sm" onclick="openAssignLocation('${equipment.id}')">
            <i data-lucide="map-pin"></i> ${escapeHtml(t('buildings.changeLocation'))}
          </button>
        </div>
      `
    }

    // AI Scan Data — показываем сохранённые данные если есть
    const aiSection = document.getElementById('aiScanDataSection')
    const aiContent = document.getElementById('aiScanDataContent')

    // Проверяем существование элементов, прежде чем менять стили
    if (aiSection && aiContent) {
      if (equipment.ai_scan_data) {
        aiSection.style.display = 'block'
        aiContent.innerHTML = `<pre style="white-space:pre-wrap; font-size:13px; font-family:monospace;">${equipment.ai_scan_data}</pre>`
      } else {
        aiSection.style.display = 'none'
        aiContent.innerHTML = ''
      }
    }
	const photoContainer = document.getElementById('detailPhotoContainer')
    const photoImg = document.getElementById('detailEquipmentPhoto')
    
    if (photoContainer && photoImg) {
      if (equipment.photo_url) {
        photoContainer.style.display = 'block'
        photoImg.src = equipment.photo_url
      } else {
        photoContainer.style.display = 'none'
        photoImg.src = ''
      }
    }  

    // Открываем модалку (используем flex, как в твоем CSS)
    const detailModal = document.getElementById('equipmentDetailModal')
    if (detailModal) {
      detailModal.style.display = 'flex'
    }

    // Service journal for this equipment — fire and forget, it renders itself
    loadEquipmentServiceHistory(equipment.id)
    
    refreshIcons()
    
  } catch (error) {
    console.error('Error loading equipment detail:', error)
    alert('Error loading equipment details')
  }
}
async function deleteEquipment() {
  if (!currentEquipment) return;

  const confirmDelete = confirm(`Are you sure you want to delete "${currentEquipment.name}"? This action cannot be undone.`);
  if (!confirmDelete) return;

  try {
    deleteEquipmentBtn.disabled = true;
    deleteEquipmentBtn.textContent = 'Deleting...';

    // 1. Освобождаем NFC тэг в базе
    if (currentEquipment.nfc_id) {
      await supabase
        .from('nfc_tags')
        .update({
          equipment_id: null,
          is_assigned: false,
          assigned_at: null
        })
        .eq('nfc_id', currentEquipment.nfc_id);
    }

    // 2. Мягкое удаление (Soft Delete)
    const { error } = await supabase
      .from('equipment')
      .update({ 
        deleted_at: new Date().toISOString(),
        nfc_id: null 
      })
      .eq('id', currentEquipment.id);

    if (error) throw error;

    alert('✅ Equipment deleted and tag released!');
    
    // Закрываем всё и обновляем экран
    if (typeof closeDetailModalFn === 'function') closeDetailModalFn();
    await loadEquipmentList();
    await loadStats();

  } catch (error) {
    console.error('Delete error:', error);
    alert('Error: ' + error.message);
  } finally {
    deleteEquipmentBtn.disabled = false;
    deleteEquipmentBtn.innerHTML = '<i data-lucide="trash-2"></i> Delete';
    if (typeof refreshIcons === 'function') refreshIcons();
  }
}
// Helper to render detail item
function renderDetailItem(elementId, label, value) {
  const element = document.getElementById(elementId)
  const valueClass = value ? 'detail-value' : 'detail-value empty'
  const displayValue = value || t('common.notSpecified')
  
  element.innerHTML = `
    <div class="detail-label">${label}</div>
    <div class="${valueClass}">${displayValue}</div>
  `
}
deleteEquipmentBtn?.addEventListener('click', deleteEquipment);
// Open edit modal
editEquipmentBtn?.addEventListener('click', async () => {
  if (!currentEquipment) return
  const previewEdit = document.getElementById('photoPreviewEdit');
  const placeholderEdit = document.getElementById('photoPlaceholderEdit');
  const removeBtnEdit = document.getElementById('removePhotoBtnEdit');
  const fileInputEdit = document.getElementById('editEquipmentPhotoInput');
  if (fileInputEdit) fileInputEdit.value = '';
  if (currentEquipment.photo_url) {
    previewEdit.src = currentEquipment.photo_url;
    previewEdit.style.display = 'block';
    placeholderEdit.style.display = 'none';
    removeBtnEdit.style.display = 'flex';
  } else {
	previewEdit.src = '';  
    previewEdit.style.display = 'none';
    placeholderEdit.style.display = 'flex';
    removeBtnEdit.style.display = 'none';
	
  }
  isPhotoRemoved = false; // Сбрасываем при каждом открытии
  // Populate edit form
  document.getElementById('editEquipmentName').value = currentEquipment.name
  document.getElementById('editClientCompany').value = currentEquipment.client_company || ''
  document.getElementById('editSerialNumber').value = currentEquipment.serial_number || ''
  document.getElementById('editLocation').value = currentEquipment.location || ''
  document.getElementById('editEquipmentPrice').value = currentEquipment.price != null ? currentEquipment.price : ''
  document.getElementById('editStatus').value = currentEquipment.status
  document.getElementById('edit_ai_scan_data').value = currentEquipment.ai_scan_data || ''	
  // Load checklist
  await loadChecklistsForDropdowns()
  if (currentEquipment.checklist_id) {
    document.getElementById('editChecklistSelect').value = currentEquipment.checklist_id
  }
  await loadSpecialistsForDropdown(currentEquipment.assigned_specialist_id)

  // Building / zone + maintenance interval
  populateLocationSelects(
    'editBuildingSelect',
    'editZoneSelect',
    currentEquipment.building_id || null,
    currentEquipment.zone_id || null
  )
  document.getElementById('editMaintenanceInterval').value =
    currentEquipment.maintenance_interval_days ?? ''

  // Load NFC tags
  loadAvailableTagsForEdit()
  
  // Generate dynamic fields for editing
  const editDynamicFields = document.getElementById('editDynamicFields')
  editDynamicFields.innerHTML = ''
  
  const editSchema = currentEquipment.equipment_type.fields_schema || {}
  const editCustomFields = currentEquipment.custom_fields || {}
  const editBaseFields = ['name', 'client_company', 'location', 'checklist_id', 'nfc_id', 'status']

  // Fields managed automatically by the work log system — not manually editable
  // Fields managed automatically by the work log system — not manually editable in edit form
  // Note: next_inspection_date and inspection_deadline ARE editable (admin sets MOT date manually)
  const maintDateFields = ['last_maintenance_date', 'next_maintenance_date', 'last_inspection_date']
  const allSchemaSections = Object.values(editSchema).filter(Array.isArray)
  allSchemaSections.flat().forEach(field => {
    if (editBaseFields.includes(field.id)) return
    if (maintDateFields.includes(field.id)) return
    const currentValue = editCustomFields[field.id] ?? currentEquipment[field.id] ?? ''
    editDynamicFields.innerHTML += generateEditFieldHTML(field, currentValue)
  })
  
  // Show edit modal
  equipmentDetailModal.style.display = 'none'
  editEquipmentModal.style.display = 'flex'
})

// Generate edit field HTML with current value
function generateEditFieldHTML(field, currentValue) {
  const required = field.required ? '*' : ''
  const requiredAttr = field.required ? 'required' : ''
  const safeValue = escapeHtml(currentValue ?? '')

  let inputHTML = ''

  switch (field.type) {
    case 'text':
      inputHTML = `
        <input
          type="text"
          id="edit_field_${field.id}"
          value="${safeValue}"
          ${requiredAttr}
        >
      `
      break

    case 'number':
      inputHTML = `
        <input
          type="number"
          id="edit_field_${field.id}"
          value="${safeValue}"
          ${requiredAttr}
        >
      `
      break

    case 'select':
      const options = field.options.map(opt =>
        `<option value="${escapeHtml(opt)}" ${opt === currentValue ? 'selected' : ''}>${escapeHtml(opt)}</option>`
      ).join('')
      inputHTML = `
        <select id="edit_field_${field.id}" ${requiredAttr}>
          <option value="">Select...</option>
          ${options}
        </select>
      `
      break

    case 'date':
      inputHTML = `
        <input
          type="date"
          id="edit_field_${field.id}"
          value="${safeValue}"
          ${requiredAttr}
        >
      `
      break

    default:
      inputHTML = `
        <input
          type="text"
          id="edit_field_${field.id}"
          value="${safeValue}"
          ${requiredAttr}
        >
      `
  }
  
  return `
    <div class="form-group">
      <label for="edit_field_${field.id}">${t('equipment.' + field.id) || field.label} ${required}</label>
      ${inputHTML}
    </div>
  `
}

// Load available tags for edit (including current tag)
async function loadAvailableTagsForEdit() {
  try {
    const { data: tags, error } = await supabase
      .from('nfc_tags')
      .select('*')
      .eq('tenant_id', currentUser.tenant_id)
      .or(`is_assigned.eq.false,equipment_id.eq.${currentEquipment.id}`)
      .order('nfc_id', { ascending: true })
    
    if (error) throw error
    
    const editNfcTagSelect = document.getElementById('editNfcTag')
    editNfcTagSelect.innerHTML = '<option value="">No Smart Tag</option>'
    
    tags.forEach(tag => {
      const option = document.createElement('option')
      option.value = tag.nfc_id
      option.textContent = tag.nfc_id
      if (tag.nfc_id === currentEquipment.nfc_id) {
        option.selected = true
      }
      editNfcTagSelect.appendChild(option)
    })
    
  } catch (error) {
    console.error('Error loading tags:', error)
  }
}

// Submit edit form
editEquipmentForm?.addEventListener('submit', async (e) => {
  e.preventDefault()
  
  editBtnText.textContent = 'Saving...'
  editEquipmentForm.querySelector('button[type="submit"]').disabled = true
  editEquipmentError.classList.remove('show')
  
  try {
    const name = document.getElementById('editEquipmentName').value
    const clientCompany = document.getElementById('editClientCompany').value || null
    const serialNumber = document.getElementById('editSerialNumber').value || null
    const location = document.getElementById('editLocation').value || null
    const status = document.getElementById('editStatus').value
    const newNfcId = document.getElementById('editNfcTag').value || null
    const checklist_id = document.getElementById('editChecklistSelect').value || null
    const editPriceRaw = document.getElementById('editEquipmentPrice')?.value
    const editPrice = editPriceRaw !== '' && editPriceRaw !== null && editPriceRaw !== undefined ? parseFloat(editPriceRaw) : null

    // 1. Last maintenance is managed by work logs — preserve as-is
    const lastMaint = currentEquipment.last_maintenance_date || null

    // 2. Collect custom fields from all schema sections
    const customFields = {}
    const editSchema = currentEquipment.equipment_type.fields_schema || {}
    const baseFields = ['name', 'client_company', 'location', 'status', 'nfc_id', 'checklist_id']
    const excludedDateFields = ['last_maintenance_date', 'next_maintenance_date', 'last_inspection_date']
    Object.values(editSchema).filter(Array.isArray).flat().forEach(field => {
      if (baseFields.includes(field.id)) return
      if (excludedDateFields.includes(field.id)) return
      const value = document.getElementById(`edit_field_${field.id}`)?.value
      if (value !== undefined) customFields[field.id] = value || null
    })

    // next_maintenance_date: if inspection date was edited manually, sync it; otherwise preserve
    const newInspectionDate = customFields.next_inspection_date || customFields.inspection_deadline || null

    // Building / zone + maintenance interval (Hooldusraamat)
    const editLocation = readLocationSelects('editBuildingSelect', 'editZoneSelect')
    const rawInterval = parseInt(document.getElementById('editMaintenanceInterval')?.value, 10)
    const maintenanceInterval = Number.isFinite(rawInterval) && rawInterval > 0 ? rawInterval : null

    // A manually entered inspection date always wins. Otherwise, if the admin
    // just set/changed the interval, roll the plan forward from the last service.
    const intervalChanged = maintenanceInterval !== (currentEquipment.maintenance_interval_days ?? null)
    const intervalDerivedDate = intervalChanged && lastMaint
      ? addDays(lastMaint, maintenanceInterval)
      : null

    const nextMaint = newInspectionDate || intervalDerivedDate || currentEquipment.next_maintenance_date || null
    // Логика фото для редактирования
    // Логика фото для редактирования
    const editPhotoFile = document.getElementById('editEquipmentPhotoInput')?.files[0];
    let finalPhotoUrl = currentEquipment.photo_url; 

    if (isPhotoRemoved) {
      finalPhotoUrl = null; // Если нажали на "X"
    } else if (editPhotoFile) {
      try {
        finalPhotoUrl = await uploadPhoto(editPhotoFile, currentEquipment.id);
      } catch (err) {
        console.error('Update photo error:', err);
      }
    }
    // 3. Обновление
    const { error: updateError } = await supabase
      .from('equipment')
      .update({
        name,
        client_company: clientCompany,
        serial_number: serialNumber,
        location,
        building_id: editLocation.building_id,
        zone_id: editLocation.zone_id,
        maintenance_interval_days: maintenanceInterval,
        price: editPrice,
        status,
        custom_fields: customFields,
		ai_scan_data: document.getElementById('edit_ai_scan_data')?.value || null,
		photo_url: finalPhotoUrl,  
        nfc_id: newNfcId,
        last_maintenance_date: lastMaint, // Используем переменную
        next_maintenance_date: nextMaint, // Используем переменную
        checklist_id: checklist_id,
		assigned_specialist_id: document.getElementById('editAssignedSpecialistSelect')?.value || null
      })
      .eq('id', currentEquipment.id)
    
    if (updateError) throw updateError
    
    
    // Handle NFC tag changes
    const oldNfcId = currentEquipment.nfc_id
    
    // If old tag exists and different from new, unassign it
    if (oldNfcId && oldNfcId !== newNfcId) {
      await supabase
        .from('nfc_tags')
        .update({
          equipment_id: null,
          is_assigned: false
        })
        .eq('nfc_id', oldNfcId)
    }
    
    // If new tag selected, assign it
    if (newNfcId && newNfcId !== oldNfcId) {
      await supabase
        .from('nfc_tags')
        .update({
          equipment_id: currentEquipment.id,
          is_assigned: true,
          assigned_at: new Date().toISOString()
        })
        .eq('nfc_id', newNfcId)
    }
    
    alert('✅ Equipment updated successfully!')
    
    closeEditModalFn()
    await loadEquipmentList()
    await loadStats()
    
  } catch (error) {
    console.error('Error updating equipment:', error)
    editEquipmentError.textContent = `Error: ${error.message}`
    editEquipmentError.classList.add('show')
  } finally {
    editBtnText.textContent = 'Save Changes'
    editEquipmentForm.querySelector('button[type="submit"]').disabled = false
  }
})

// Update window.openEquipmentDetail
window.openEquipmentDetail = openEquipmentDetail
// ДОБАВЬ В КОНЕЦ equipment/main.js

// Mechanics Management
const addMechanicBtn = document.getElementById('addMechanicBtn')
const addMechanicModal = document.getElementById('addMechanicModal')
const closeMechanicModal = document.getElementById('closeMechanicModal')
const cancelMechanicBtn = document.getElementById('cancelMechanicBtn')
const addMechanicForm = document.getElementById('addMechanicForm')
const addMechanicError = document.getElementById('addMechanicError')
const addMechanicBtnText = document.getElementById('addMechanicBtnText')

const mechanicCredentialsModal = document.getElementById('mechanicCredentialsModal')
const closeCredentialsModal = document.getElementById('closeCredentialsModal')
const closeCredentialsBtn = document.getElementById('closeCredentialsBtn')

// Open add mechanic modal
addMechanicBtn?.addEventListener('click', () => {
  addMechanicModal.style.display = 'flex'
  addMechanicForm.reset()
  addMechanicError.classList.remove('show')
})

// Close modals
function closeMechanicModalFn() {
  addMechanicModal.style.display = 'none'
}

function closeCredentialsModalFn() {
  mechanicCredentialsModal.style.display = 'none'
  loadMechanicsList() // Refresh list
}

closeMechanicModal?.addEventListener('click', closeMechanicModalFn)
cancelMechanicBtn?.addEventListener('click', closeMechanicModalFn)
addMechanicModal?.querySelector('.modal-overlay')?.addEventListener('click', closeMechanicModalFn)

closeCredentialsModal?.addEventListener('click', closeCredentialsModalFn)
closeCredentialsBtn?.addEventListener('click', closeCredentialsModalFn)

// Submit mechanic form
addMechanicForm?.addEventListener('submit', async (e) => {
  e.preventDefault()
  
  addMechanicBtnText.textContent = 'Creating...'
  addMechanicForm.querySelector('button[type="submit"]').disabled = true
  addMechanicError.classList.remove('show')
  
  try {
    const fullName = document.getElementById('mechanicFullName').value
    const email = document.getElementById('mechanicEmail').value || null
    const phone = document.getElementById('mechanicPhone').value || null
    const role = 'mechanic'
    
    // Generate username: companyname.firstname
    const companySlug = currentUser.tenant.company_name
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
      .substring(0, 10)
    
    const firstNameSlug = fullName.split(' ')[0]
      .toLowerCase()
      .replace(/[^a-z0-9]/g, '')
    
    const username = `${companySlug}.${firstNameSlug}`
    
    // Generate password: 12 random characters
    const password = generateStrongPassword(12)

    // Hash password with bcrypt before storing
    const { data: hashedPassword, error: hashError } = await supabase.rpc('hash_password', { plaintext: password })
    if (hashError) throw new Error('Failed to hash password: ' + hashError.message)

    // Create mechanic user
    const { data: mechanic, error: mechanicError } = await supabase
      .from('users')
      .insert([{
        tenant_id: currentUser.tenant_id,
        username: username,
        password_hash: hashedPassword,
        full_name: fullName,
        email: email,
        phone: phone,
        role: role,
        is_active: true,
        must_change_password: true,
        language: 'en'
      }])
      .select()
      .single()
    
    if (mechanicError) throw mechanicError
    
    // Show credentials
	document.getElementById('mechanicFullNameDisplay').textContent = fullName  
    document.getElementById('generatedUsername').textContent = username
    document.getElementById('generatedPassword').textContent = password
    
    closeMechanicModalFn()
    mechanicCredentialsModal.style.display = 'flex'
    
    
    

closeMechanicModalFn()
mechanicCredentialsModal.style.display = 'flex'
    // TODO: Send email/SMS with credentials
    
  } catch (error) {
    console.error('Error creating mechanic:', error)
    addMechanicError.textContent = `Error: ${error.message}`
    addMechanicError.classList.add('show')
  } finally {
    addMechanicBtnText.textContent = 'Create Mechanic'
    addMechanicForm.querySelector('button[type="submit"]').disabled = false
  }
})

window.copyToClipboard = copyToClipboard

// Load mechanics list
async function loadMechanicsList() {
  const container = document.getElementById('mechanicsList')

  try {
    const { data: mechanics, error } = await supabase
      .from('users')
      .select('*')
      .eq('tenant_id', currentUser.tenant_id)
      .in('role', ['mechanic', 'auditor'])
      .order('created_at', { ascending: false })

    if (error) throw error

    if (!mechanics || mechanics.length === 0) {
      container.innerHTML = `<p class="loading">${t('mechanic.noMechanicsYet')}</p>`
      return
    }

    // Equipment count per specialist, for the clickable badge below —
    // the only place admin can currently see the specialist → equipment
    // direction (the reverse, equipment → specialist, already exists as
    // the "Specialist" filter on the Equipment tab).
    const { data: assignments } = await supabase
      .from('equipment')
      .select('assigned_specialist_id')
      .eq('tenant_id', currentUser.tenant_id)
      .not('assigned_specialist_id', 'is', null)

    const equipmentCountBySpecialist = {}
    ;(assignments || []).forEach(({ assigned_specialist_id }) => {
      equipmentCountBySpecialist[assigned_specialist_id] = (equipmentCountBySpecialist[assigned_specialist_id] || 0) + 1
    })

    // Build table
    let html = `
      <table class="mechanics-table">
        <thead>
          <tr>
            <th>Name</th>
            <th>Username</th>
            <th>Contact</th>
            <th>Equipment</th>
            <th>Status</th>
            <th>Last Login</th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
    `
    
    mechanics.forEach(mechanic => {
      const statusClass = mechanic.is_active ? 'active' : 'inactive'
      const statusText = mechanic.is_active ? 'Active' : 'Inactive'
      
      const contactParts = []
      if (mechanic.email) contactParts.push(mechanic.email)
      if (mechanic.phone) contactParts.push(mechanic.phone)
      const contact = contactParts.length > 0 ? contactParts.join('<br>') : '-'
      
      const lastLogin = mechanic.last_login_at
        ? new Date(mechanic.last_login_at).toLocaleDateString()
        : 'Never'
      const equipCount = equipmentCountBySpecialist[mechanic.id] || 0
      const equipCell = equipCount > 0
        ? `<button type="button" class="mechanic-equip-badge" onclick="window.showEquipmentForSpecialist('${mechanic.id}')">${equipCount}</button>`
        : '<span class="text-muted">—</span>'
      const actionButtons = `
       <button class="btn-reset" onclick="window.resetMechanicPassword('${mechanic.id}', '${mechanic.full_name.replace(/'/g, "\\'")}', '${mechanic.username}')"><i data-lucide="key"></i> Reset</button>
       ${mechanic.is_active
         ? `<button class="btn-deactivate" onclick="toggleMechanicStatus('${mechanic.id}', false)">${t('mechanic.deactivateBtn')}</button>`
         : `<button class="btn-activate" onclick="toggleMechanicStatus('${mechanic.id}', true)">${t('mechanic.activateBtn')}</button>`
       }
     `
      
      html += `
        <tr>
          <td><strong>${mechanic.full_name}</strong></td>
          <td><code>${mechanic.username}</code></td>
          <td>${contact}</td>
          <td>${equipCell}</td>
          <td><span class="mechanic-status ${statusClass}">${statusText}</span></td>
          <td>${lastLogin}</td>
          <td>${actionButtons}</td>
        </tr>
      `
    })
    
    html += '</tbody></table>'
    container.innerHTML = html
    refreshIcons()
    
  } catch (error) {
    console.error('Error loading mechanics:', error)
    container.innerHTML = '<p class="loading">Error loading mechanics</p>'
  }
}

// Toggle mechanic status (activate/deactivate)
async function toggleMechanicStatus(mechanicId, newStatus) {
  const action = newStatus ? 'activate' : 'suspend'
  const confirmKey = action === 'suspend' ? 'confirm.suspendMechanic' : 'confirm.activateMechanic'
  
  if (!confirm(t(confirmKey))) {
    return
  }
  
  try {
    const { error } = await supabase
      .from('users')
      .update({ is_active: newStatus })
      .eq('id', mechanicId)
    
    if (error) throw error
    
    await loadMechanicsList()
    
  } catch (error) {
    console.error('Error toggling mechanic status:', error)
    alert(t('error.prefix') + error.message)
  }
}

window.toggleMechanicStatus = toggleMechanicStatus
// ==================== OPEN AS MECHANIC ====================
document.getElementById('openAsMechanicBtn')?.addEventListener('click', () => {
  if (!currentEquipment) return

  const urlCode = currentEquipment.nfc_tag?.url_code
  if (!urlCode) {
    alert(t('equipment.openAsMechanicNoTag'))
    return
  }

  // Write short-lived passthrough token so mechanic page can auto-login
  const passthrough = {
    userId: currentUser.id,
    username: currentUser.username,
    fullName: currentUser.full_name || currentUser.username,
    tenantId: currentUser.tenant_id,
    role: currentUser.role,
    sessionToken: sessionToken,
    timestamp: Date.now()
  }
  localStorage.setItem('kixik_admin_passthrough', JSON.stringify(passthrough))

  window.open(`/e/${urlCode}`, '_blank')
})

// ==================== TAB NAVIGATION ====================
document.querySelectorAll('.tab-btn').forEach(btn => {
  btn.addEventListener('click', () => {
    switchToTab(btn.dataset.tab)
  })
})
// ==================== CHECKLIST SAVE (INLINE) ====================

document.addEventListener('click', (e) => {
  if (e.target.id === 'addChecklistBtn' || e.target.id === 'createFirstChecklistBtn') {
    console.log('Open checklist modal clicked')
    const modal = document.getElementById('checklistModal')
    if (modal) {
      modal.style.display = 'flex'
      document.getElementById('checklistForm').reset()
      document.getElementById('checklistModalTitle').textContent = 'Create Checklist'
      document.getElementById('checklistId').value = ''
      
      // Add first item
      const container = document.getElementById('checklistItemsList')
      container.innerHTML = `
        <div class="checklist-item-row">
          <span class="item-number">1.</span>
          <input type="text" class="checklist-item-input" placeholder="e.g., Check oil level" required>
          <button type="button" class="btn-icon btn-remove-item" onclick="this.parentElement.remove()">×</button>
        </div>
      `
    }
  }
  
  if (e.target.id === 'addChecklistItemBtn') {
    const container = document.getElementById('checklistItemsList')
    const count = container.children.length + 1
    const div = document.createElement('div')
    div.className = 'checklist-item-row'
    div.innerHTML = `
      <span class="item-number">${count}.</span>
      <input type="text" class="checklist-item-input" placeholder="e.g., Check oil level" required>
      <button type="button" class="btn-icon btn-remove-item" onclick="this.parentElement.remove()">×</button>
    `
    container.appendChild(div)
  }
  
  if (e.target.id === 'closeChecklistModal' || e.target.id === 'cancelChecklistBtn') {
    document.getElementById('checklistModal').style.display = 'none'
  }
})

// Checklist form submit
const checklistFormHandler = async (e) => {
  e.preventDefault()
  console.log('FORM SUBMIT TRIGGERED!')
  
  const nameEl = document.getElementById('checklistName')
  const descEl = document.getElementById('checklistDescription')
  
  console.log('nameEl:', nameEl)
  console.log('descEl:', descEl)
  
  if (!nameEl) {
    alert('ERROR: Form elements not found! Modal did not open correctly.')
    console.error('checklistName element is NULL')
    return
  }
  
  const name = nameEl.value ? nameEl.value.trim() : ''
  const description = descEl && descEl.value ? descEl.value.trim() : ''
  
  console.log('Name:', name)
  console.log('Description:', description)
  
  
  if (!name) {
    alert(t('alert.enterChecklistName'))
    return
  }
  
  // Collect items
  const inputs = document.querySelectorAll('.checklist-item-input')
  const sections = []
  
  console.log('Found inputs:', inputs.length)
  
  inputs.forEach((input, i) => {
    const title = input.value ? input.value.trim() : ''
    if (title) {
      sections.push({ id: `${i+1}`, title: title, required: true })
    }
  })
  
  console.log('Sections:', sections)
  
  if (sections.length === 0) {
    alert(t('alert.addAtLeastOneItem'))
    return
  }
  
  try {
    const data = {
      tenant_id: currentUser.tenant_id,
      name: name,
      description: description || null,
      sections: sections,
      is_active: true,
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString()
    }
    
    console.log('Saving:', data)
    
    const { error } = await supabase
      .from('checklists')
      .insert([data])

    if (error) throw error
    alert(t('alert.checklistCreated'))
    document.getElementById('checklistModal').style.display = 'none'
// Reload checklists
    const { loadChecklists } = await import('./checklist-functions.js')
    await loadChecklists()
    
  } catch (error) {
    console.error('Error:', error)
    alert('Error: ' + error.message)
  }
}
// Attach handler
setTimeout(() => {
  const form = document.getElementById('checklistForm')
  if (form && !form.hasAttribute('data-handler-attached')) {
    console.log('Attaching inline handler to form (ONCE)')
    form.setAttribute('data-handler-attached', 'true')
    form.addEventListener('submit', checklistFormHandler)
  }
}, 1000)
// ==================== DOWNLOAD MECHANIC CREDENTIALS ====================

/**
 * Download mechanic credentials as TXT file
 */
function downloadMechanicCredentials() {
  const name = document.getElementById('mechanicFullNameDisplay').textContent
  const username = document.getElementById('generatedUsername').textContent
  const password = document.getElementById('generatedPassword').textContent
  const company = currentUser.tenant?.company_name || t('common.company')
  
  const content = `${t('mechanic.credentialsFileTitle')}
${'='.repeat(50)}

${t('mechanic.credentialsFileCompany')} ${company}
${t('mechanic.credentialsFileName')} ${name}
${t('mechanic.credentialsFileUsername')} ${username}
${t('mechanic.credentialsFilePassword')} ${password}

${t('mechanic.credentialsFileCreated')} ${new Date().toLocaleString()}

${'='.repeat(50)}

${t('mechanic.credentialsFileNotesTitle')}
- ${t('mechanic.credentialsFileNote1')}
- ${t('mechanic.credentialsFileNote2')}
- ${t('mechanic.credentialsFileNote3')}
- ${t('mechanic.credentialsFileNote4')}

${t('mechanic.credentialsFileAccessTitle')}
- ${t('mechanic.credentialsFileAccess1')}
- ${t('mechanic.credentialsFileAccess2')}
- ${t('mechanic.credentialsFileAccess3')}

${'='.repeat(50)}
${t('mechanic.credentialsFileFooter')}
`
  
  // Create blob and download
  const blob = new Blob([content], { type: 'text/plain;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `mechanic_${username}_credentials.txt`
  document.body.appendChild(a)
  a.click()
  document.body.removeChild(a)
  URL.revokeObjectURL(url)
  
  console.log('Credentials downloaded:', username)
}

// Make function globally available
window.downloadMechanicCredentials = downloadMechanicCredentials

// ==================== DUPLICATE EQUIPMENT ====================

/**
 * Duplicate current equipment
 * Opens the add equipment form with all fields pre-filled
 */
// ==================== DUPLICATE EQUIPMENT ====================
const duplicateEquipmentBtn = document.getElementById('duplicateEquipmentBtn')

duplicateEquipmentBtn?.addEventListener('click', async () => {
  if (!currentEquipment) return
  
  // 1. Проверяем тип
  selectedEquipmentType = currentEquipment.equipment_type
  if (!selectedEquipmentType || !selectedEquipmentType.id) {
    alert(t('error.equipmentTypeMissing'))
    return
  }

  // 2. Открываем окно
  equipmentDetailModal.style.display = 'none'
  addEquipmentModal.style.display = 'flex'
  step1.style.display = 'none'
  step2.style.display = 'block'

  // 3. Заголовок и иконка (исправил эмодзи на иконки кодом)
  const iconCode = selectedEquipmentType.icon || 'package'
  // Если в базе все еще эмодзи, меняем их на лету для иконок
  const safeIcon = (iconCode.length < 3) ? getLucideIcon(selectedEquipmentType.code) : iconCode
  
  selectedTypeName.innerHTML = `<i data-lucide="${safeIcon}"></i> ${selectedEquipmentType.name}`
  refreshIcons()

  // 4. Заполняем БАЗОВЫЕ поля (Name, Client, Location)
  document.getElementById('name').value = `${currentEquipment.name} ${t('equipment.copySuffix')}`
  
  const clientInput = document.getElementById('clientCompany')
  if (clientInput) clientInput.value = currentEquipment.client_company || ''
  
  const locInput = document.getElementById('location')
  if (locInput) locInput.value = currentEquipment.location || ''

  // Сбрасываем серийник
  const serialInput = document.getElementById('serialNumber')
  if (serialInput) serialInput.value = ''

  // Статус
  const statusSelect = document.getElementById('status')
  if (statusSelect) {
     const curStatus = (currentEquipment.status || 'active').toLowerCase()
     let found = false
     for(let i=0; i<statusSelect.options.length; i++) {
        if(statusSelect.options[i].value.toLowerCase() === curStatus) {
           statusSelect.selectedIndex = i
           found = true; break;
        }
     }
     if(!found) statusSelect.value = 'active'
  }

  // Теги и Чеклисты
  await loadAvailableTags()
  if (currentEquipment.checklist_id) {
    await loadChecklistsForDropdowns()
    const checklistSelect = document.getElementById('checklistSelect')
    if (checklistSelect) checklistSelect.value = currentEquipment.checklist_id
  }

  // 5. ЗАПОЛНЯЕМ ДИНАМИЧЕСКИЕ ПОЛЯ (Passport + Maintenance)
  const passportContainer = document.getElementById('passportFields')
  const maintenanceContainer = document.getElementById('maintenanceFields')
  
  // Очищаем старые поля
  if (passportContainer) passportContainer.querySelectorAll('.dynamic-field').forEach(el => el.remove())
  if (maintenanceContainer) maintenanceContainer.querySelectorAll('.dynamic-field').forEach(el => el.remove())

  const schema = selectedEquipmentType.fields_schema || {}
  const customFieldsData = currentEquipment.custom_fields || {}
  console.log('🔍 custom_fields:', JSON.stringify(customFieldsData))

  // Маппинг старых IT-ключей на ключи схемы
  const itLegacyMap = {
    'cpu_specs': 'cpu',
    'ram_gb': 'ram',
    'storage_specs': 'disk',
    'os_version': 'os',
    'assigned_user': 'user',
    'ip_address': 'ip'
  }

  // Помощник: ищет значение по ключу схемы, потом по legacy-ключу, потом в корне объекта
  const findVal = (fieldId) => {
    let val = customFieldsData[fieldId]
    if (val === undefined && itLegacyMap[fieldId]) val = customFieldsData[itLegacyMap[fieldId]]
    if (val === undefined) val = currentEquipment[fieldId]
    return val
  }

  // --- PASSPORT ---
  if (schema.passport && passportContainer) {
    schema.passport.forEach(field => {
      if (['name', 'client_company'].includes(field.id)) return 
      passportContainer.insertAdjacentHTML('beforeend', generateFieldHTMLWithValue(field, findVal(field.id)))
    })
  }

  // --- MAINTENANCE ---
  if (schema.maintenance && maintenanceContainer) {
    let maintenanceHTML = ''
    schema.maintenance.forEach(field => {
      if (['location', 'checklist_id', 'nfc_id', 'status'].includes(field.id)) return
      maintenanceHTML += generateFieldHTMLWithValue(field, findVal(field.id))
    })
    // Вставляем В НАЧАЛО (перед Location)
    maintenanceContainer.insertAdjacentHTML('afterbegin', maintenanceHTML)
  }
  
  console.log('✅ Duplicated with values')
})
// Функция генерирует HTML сразу с заполненным значением
function generateFieldHTMLWithValue(field, currentValue) {
  const required = field.required ? '*' : ''
  const requiredAttr = field.required ? 'required' : ''
  const val = (currentValue !== undefined && currentValue !== null) ? currentValue : ''
  const safeVal = escapeHtml(val)
  const fieldId = `field_${field.id}`

  let inputHTML = ''

  switch (field.type) {
    case 'text':
      inputHTML = `<input type="text" id="${fieldId}" value="${safeVal}" ${requiredAttr}>`
      break
    case 'number':
      inputHTML = `<input type="number" id="${fieldId}" value="${safeVal}" ${requiredAttr}>`
      break
    case 'textarea':
      inputHTML = `<textarea id="${fieldId}" rows="3" ${requiredAttr}>${safeVal}</textarea>`
      break
    case 'date':
      inputHTML = `<input type="date" id="${fieldId}" value="${safeVal}" ${requiredAttr}>`
      break
    case 'select':
      const options = (field.options || []).map(opt => {
        const isSelected = String(opt) === String(val) ? 'selected' : ''
        return `<option value="${escapeHtml(opt)}" ${isSelected}>${escapeHtml(opt)}</option>`
      }).join('')

      inputHTML = `
        <select id="${fieldId}" ${requiredAttr}>
          <option value="" data-i18n="common.select">Select...</option>
          ${options}
        </select>`
      break
    default:
      inputHTML = `<input type="text" id="${fieldId}" value="${safeVal}" ${requiredAttr}>`
  }

  const labelText = t(`equipment.${field.id}`) || field.label

  return `
    <div class="form-group dynamic-field">
      <label for="${fieldId}">${labelText} ${required}</label>
      ${inputHTML}
    </div>
  `
}
// ==================== LANGUAGE SWITCHER ====================

// Initialize language switcher
document.addEventListener('DOMContentLoaded', () => {
  const languageSelect = document.getElementById('languageSelect')
 
   if (languageSelect) {
     // Set initial value from localStorage
     const currentLang = localStorage.getItem('kixik_language') || 'en'
     languageSelect.value = currentLang
     
     // Listen for changes
     languageSelect.addEventListener('change', (e) => {
       setLanguage(e.target.value)
     })
   }

  // Initial translation update
  updateTranslations()
})

// Listen for language changes to update dynamic content
window.addEventListener('languageChanged', () => {
  // Re-translate any dynamic content
  // This will be called when user switches language
  console.log('Language changed, updating UI...')
})
// ==================== MAKE I18N GLOBALLY AVAILABLE ====================
// Export i18n functions to window for global access
window.t = t
window.setLanguage = setLanguage
window.updateTranslations = updateTranslations

// ==================== LANGUAGE SWITCHER ====================
// Initialize language switcher
document.addEventListener('DOMContentLoaded', () => {
  console.log('🌍 Initializing language switcher...')
  
  const langButtons = document.querySelectorAll('.lang-btn')
  console.log('Found language buttons:', langButtons.length)
  
  langButtons.forEach(btn => {
    btn.addEventListener('click', () => {
      const lang = btn.dataset.lang
      console.log('Language button clicked:', lang)
      setLanguage(lang)
    })
  })
  
  // Initial translation update - КРИТИЧЕСКИ ВАЖНО!
  console.log('🔄 Running initial updateTranslations()')
  updateTranslations()
})

// Listen for language changes to update dynamic content
window.addEventListener('languageChanged', () => {
  console.log('Language changed event received, updating UI...')
  updateTranslations()
})
// ==================== OVERDUE MAINTENANCE ====================

/**
 * Загрузить количество просроченного оборудования
 */
async function loadOverdueCount() {
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    const in14days = today + 14 * 24 * 60 * 60 * 1000;

    const { data: equipment, error } = await supabase
      .from('equipment')
      .select('next_maintenance_date, status')
      .eq('tenant_id', currentUser.tenant_id)
      .eq('status', 'active')
      .not('next_maintenance_date', 'is', null);

    if (error) throw error;

    const overdueItems = equipment.filter(eq => {
      const mTime = new Date(new Date(eq.next_maintenance_date).toDateString()).getTime();
      return mTime < today;
    });
    const upcomingItems = equipment.filter(eq => {
      const mTime = new Date(new Date(eq.next_maintenance_date).toDateString()).getTime();
      return mTime >= today && mTime <= in14days;
    });

    // "Needs attention" is the 14-day plan, and this query already has it
    const upcomingEl = document.getElementById('upcomingCount');
    if (upcomingEl) upcomingEl.textContent = upcomingItems.length;

    const count = overdueItems.length;
    const overdueCountEl = document.getElementById('overdueCount');
    const overdueCard = document.getElementById('overdueCard');
    const subtextEl = document.getElementById('overdueSubtext');

    if (overdueCountEl) overdueCountEl.textContent = count;

    if (subtextEl) {
      subtextEl.textContent = count === 0 ? 'All up to date' : `${count} item${count > 1 ? 's' : ''} past due date`
    }

    if (overdueCard) {
      overdueCard.classList.remove('has-overdue', 'no-overdue');
      overdueCard.classList.add(count > 0 ? 'has-overdue' : 'no-overdue');
    }

  } catch (error) {
    console.error('Error in loadOverdueCount:', error);
  }
}

let srSpecialistsCache = null
let srRequestsMap = {}

function srEscapeHtml(text) {
  if (!text) return ''
  const div = document.createElement('div')
  div.textContent = String(text)
  return div.innerHTML
}

async function loadServiceRequestsCount() {
  try {
    const { data, error } = await supabase
      .from('service_requests')
      .select('id, status, assigned_to, created_at')
      .eq('tenant_id', currentUser.tenant_id)
      .in('status', ['new', 'assigned', 'in_progress'])

    if (error) throw error

    const count = data?.length || 0
    const staleThreshold = Date.now() - 3 * 24 * 60 * 60 * 1000
    const unassigned = data?.filter(r => !r.assigned_to).length || 0
    const stale = data?.filter(r => new Date(r.created_at).getTime() < staleThreshold).length || 0

    const countEl = document.getElementById('serviceRequestsCount')
    const card = document.getElementById('serviceRequestsCard')
    const subtextEl = document.getElementById('srSubtext')

    if (countEl) countEl.textContent = count

    if (subtextEl) {
      const parts = []
      if (unassigned > 0) parts.push(`${unassigned} unassigned`)
      if (stale > 0) parts.push(`${stale} stale >3d`)
      subtextEl.textContent = parts.length ? parts.join(' · ') : (count === 0 ? 'No open requests' : '')
    }

    if (card) {
      card.classList.remove('has-overdue', 'no-overdue')
      card.classList.add(count > 0 ? 'has-overdue' : 'no-overdue')
    }

  } catch (error) {
    console.error('Error loading service requests count:', error)
  }
}

async function loadServiceRequestsModal() {
  const content = document.getElementById('serviceRequestsContent')
  if (!content) return

  const filter = content.dataset.filter || 'active'
  content.innerHTML = `<p class="loading">${t('common.loading')}</p>`

  try {
    // Load specialists list once (cached per session)
    if (!srSpecialistsCache) {
      const { data: specs } = await supabase
        .from('users')
        .select('id, full_name, email')
        .eq('tenant_id', currentUser.tenant_id)
        .order('full_name')
      srSpecialistsCache = specs || []
    }

    const statusFilter = filter === 'active'
      ? ['new', 'assigned', 'in_progress']
      : filter === 'done'
        ? ['done']
        : ['new', 'assigned', 'in_progress', 'done']

    const { data, error } = await supabase
      .from('service_requests')
      .select('*')
      .eq('tenant_id', currentUser.tenant_id)
      .in('status', statusFilter)
      .order('created_at', { ascending: false })

    if (error) throw error

    // Sort by priority: high → medium → low, then by date
    const priorityOrder = { high: 0, medium: 1, low: 2 }
    data?.sort((a, b) => {
      const pa = priorityOrder[a.priority] ?? 1
      const pb = priorityOrder[b.priority] ?? 1
      return pa !== pb ? pa - pb : 0
    })

    // Render tabs
    let html = `<div class="sr-tabs">
      <button class="sr-tab ${filter === 'active' ? 'active' : ''}" onclick="setServiceRequestFilter('active')">${t('serviceRequests.filterActive')}</button>
      <button class="sr-tab ${filter === 'done' ? 'active' : ''}" onclick="setServiceRequestFilter('done')">${t('serviceRequests.filterDone')}</button>
      <button class="sr-tab ${filter === 'all' ? 'active' : ''}" onclick="setServiceRequestFilter('all')">${t('serviceRequests.filterAll')}</button>
    </div>`

    if (!data || data.length === 0) {
      const emptyKey = filter === 'done' ? 'serviceRequests.noRequestsDone' : 'serviceRequests.noRequests'
      html += `<p class="empty-state">${t(emptyKey)}</p>`
      content.innerHTML = html
      return
    }

    // Store requests for click handlers
    srRequestsMap = {}
    data.forEach(req => { srRequestsMap[req.id] = req })

    const staleThreshold = Date.now() - 3 * 24 * 60 * 60 * 1000

    html += '<div class="sr-list">'
    data.forEach(req => {
      const date = new Date(req.created_at).toLocaleDateString()
      const ageMs = Date.now() - new Date(req.created_at).getTime()
      const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24))
      const ageHours = Math.floor(ageMs / (1000 * 60 * 60))
      const isStale = req.status !== 'done' && new Date(req.created_at).getTime() < staleThreshold
      const ageLabel = ageDays >= 1 ? `${ageDays}d ago` : `${ageHours}h ago`

      let statusLabel = t('serviceRequests.statusNew')
      let statusClass = 'status-new'
      if (req.status === 'assigned') { statusLabel = t('serviceRequests.statusAssigned'); statusClass = 'status-assigned' }
      if (req.status === 'in_progress') { statusLabel = t('serviceRequests.statusInProgress'); statusClass = 'status-inprogress' }
      if (req.status === 'done') { statusLabel = t('serviceRequests.statusDone'); statusClass = 'status-done' }

      html += `<div class="sr-item">`

      const priority = req.priority || 'medium'
      const priorityClass = `priority-${priority}`
      const priorityLabel = t(`serviceRequests.priority${priority.charAt(0).toUpperCase() + priority.slice(1)}`)

      html += `<div class="sr-item-header">
        <strong>${srEscapeHtml(req.equipment_name || '—')}</strong>
        <div class="sr-item-badges">
          <span class="badge-priority ${priorityClass}">${priorityLabel}</span>
          <span class="badge ${statusClass}">${statusLabel}</span>
          ${isStale ? `<span class="stale-badge">⚠ stale ${ageDays}d</span>` : `<span class="age-badge">${ageLabel}</span>`}
        </div>
      </div>`

      html += `<div class="sr-item-body">
        <div class="sr-item-row"><i data-lucide="map-pin"></i> ${srEscapeHtml(req.equipment_location || '—')}</div>
        <div class="sr-item-row"><i data-lucide="user"></i> ${srEscapeHtml(req.requester_name)} · ${srEscapeHtml(req.requester_contact || '—')}</div>
        <div class="sr-item-row sr-problem"><i data-lucide="message-square"></i> ${srEscapeHtml(req.problem_description)}</div>
      </div>`

      // Resolved / assigned info
      const assignedUser = req.assigned_to
        ? srSpecialistsCache.find(s => s.id === req.assigned_to)
        : null

      if (req.status === 'done') {
        const resolvedDate = req.resolved_at
          ? new Date(req.resolved_at).toLocaleDateString()
          : '—'
        const resolvedName = assignedUser ? assignedUser.full_name : '—'
        html += `<div class="sr-item-resolved">
          <i data-lucide="check-circle"></i>
          ${t('serviceRequests.resolvedBy')}: <strong>${srEscapeHtml(resolvedName)}</strong> · ${resolvedDate}
        </div>`
      } else if ((req.status === 'assigned' || req.status === 'in_progress') && assignedUser) {
        html += `<div class="sr-item-assigned">
          <i data-lucide="user-check"></i> ${t('serviceRequests.assignedTo')}: <strong>${srEscapeHtml(assignedUser.full_name)}</strong>
        </div>`
      }

      html += `<div class="sr-item-footer"><small>${t('serviceRequests.created')}: ${date}</small><div class="sr-item-actions">`

      if (req.status !== 'done') {
        const specialistOptions = srSpecialistsCache.map(s =>
          `<option value="${s.id}" ${req.assigned_to === s.id ? 'selected' : ''}>${srEscapeHtml(s.full_name)}</option>`
        ).join('')
        html += `<select class="sr-priority-select" onchange="setRequestPriority('${req.id}', this.value)" title="${t('serviceRequests.setPriority')}">
          <option value="high" ${priority === 'high' ? 'selected' : ''}>${t('serviceRequests.priorityHigh')}</option>
          <option value="medium" ${priority === 'medium' ? 'selected' : ''}>${t('serviceRequests.priorityMedium')}</option>
          <option value="low" ${priority === 'low' ? 'selected' : ''}>${t('serviceRequests.priorityLow')}</option>
        </select>
        <select class="sr-assign-select" id="sr-assign-${req.id}">
          <option value="">${t('serviceRequests.noSpecialist')}</option>
          ${specialistOptions}
        </select>
        <button class="btn btn-primary btn-sm" onclick="assignRequest('${req.id}')">${t('serviceRequests.assignAndNotify')}</button>
        <button class="btn btn-secondary btn-sm" onclick="updateRequestStatus('${req.id}', 'done')">${t('serviceRequests.markDone')}</button>`
      }

      html += `</div></div>` // actions + footer
      html += `</div>` // sr-item
    })

    html += '</div>'
    content.innerHTML = html
    refreshIcons()

  } catch (error) {
    console.error('Error loading service requests:', error)
    content.innerHTML = `<p style="color:#ef4444;padding:1rem;">Error: ${error.message}</p>`
  }
}

window.setServiceRequestFilter = function(filter) {
  const content = document.getElementById('serviceRequestsContent')
  if (content) content.dataset.filter = filter
  loadServiceRequestsModal()
}

window.updateRequestStatus = async function(requestId, status) {
  try {
    const updateData = { status, updated_at: new Date().toISOString() }
    if (status === 'done') updateData.resolved_at = new Date().toISOString()

    const { error } = await supabase
      .from('service_requests')
      .update(updateData)
      .eq('id', requestId)

    if (error) throw error

    await loadServiceRequestsModal()
    await loadServiceRequestsCount()

  } catch (error) {
    console.error('Error updating request status:', error)
    alert('Error updating status: ' + error.message)
  }
}

window.setRequestPriority = async function(requestId, priority) {
  try {
    const { error } = await supabase
      .from('service_requests')
      .update({ priority, updated_at: new Date().toISOString() })
      .eq('id', requestId)
    if (error) throw error
    // Update badge in place without full reload
    const req = srRequestsMap[requestId]
    if (req) {
      req.priority = priority
      const badge = document.querySelector(`.sr-item [data-req-id="${requestId}"] .badge-priority`)
      if (badge) {
        badge.className = `badge-priority priority-${priority}`
        badge.textContent = t(`serviceRequests.priority${priority.charAt(0).toUpperCase() + priority.slice(1)}`)
      }
    }
    // Re-sort and re-render to reflect new order
    loadServiceRequestsModal()
  } catch (error) {
    console.error('Error setting priority:', error)
    alert(t('error.prefix') + error.message)
  }
}

window.assignRequest = async function(requestId) {
  const req = srRequestsMap[requestId]
  if (!req) return

  const select = document.getElementById(`sr-assign-${requestId}`)
  const specialistId = select?.value
  if (!specialistId) {
    alert(t('serviceRequests.noSpecialist'))
    return
  }

  const specialist = srSpecialistsCache.find(s => s.id === specialistId)
  if (!specialist) return

  try {
    const { error } = await supabase
      .from('service_requests')
      .update({ status: 'assigned', assigned_to: specialistId, updated_at: new Date().toISOString() })
      .eq('id', requestId)

    if (error) throw error

    // Which client site this is for, not the maintenance company's own
    // name — client_company is set per equipment for tenants that
    // service several clients, snapshotted onto the request at creation.
    const displayCompany = req.client_company || currentUser.tenant?.company_name || '—'

    // Send notification email to specialist if they have an email
    if (specialist.email) {
      const { error: emailErr } = await supabase.functions.invoke('send-service-request-email', {
        body: {
          equipment_name: req.equipment_name || '—',
          equipment_location: req.equipment_location || '—',
          company_name: displayCompany,
          admin_email: specialist.email,
          specialist_email: null,
          specialist_name: specialist.full_name,
          priority: req.priority || 'medium',
          requester_name: req.requester_name,
          requester_contact: req.requester_contact || '—',
          problem_description: req.problem_description,
          photos: []
        }
      })
      if (emailErr) console.error('Assign email error:', emailErr)
    } else {
      console.warn('assignRequest: specialist has no email, notification skipped. Specialist:', specialist)
    }

    supabase.functions.invoke('send-telegram-notification', {
      body: {
        user_ids: [specialistId],
        text: `📋 *Task assigned to you*\n\n${req.equipment_name || '—'} — ${displayCompany}\nLocation: ${req.equipment_location || '—'}\n\n${req.problem_description || ''}`
      }
    }).catch(err => console.warn('Telegram assignment notification failed:', err))

    await loadServiceRequestsModal()
    await loadServiceRequestsCount()
    alert(t('alert.requestAssigned'))

  } catch (error) {
    console.error('Error assigning request:', error)
    alert('Error: ' + error.message)
  }
}
/**
 * Показать модальное окно с просроченным оборудованием
 */
async function showOverdueModal() {
  const modal = document.getElementById('overdueModal');
  const content = document.getElementById('overdueContent');
  if (!modal || !content) return;
  
  modal.style.display = 'flex';
  content.innerHTML = '<p class="loading">Loading...</p>';
  
  try {
    const now = new Date();
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime();
    
    const { data: equipment, error } = await supabase
      .from('equipment')
      .select(`
        id, name, location, next_maintenance_date,
        equipment_type:equipment_types(name, icon)
      `)
      .eq('tenant_id', currentUser.tenant_id)
      .eq('status', 'active')
      .not('next_maintenance_date', 'is', null);

    if (error) throw error;

    const in14days = today + 14 * 24 * 60 * 60 * 1000;

    const overdueList = equipment.filter(eq => {
      const mTime = new Date(new Date(eq.next_maintenance_date).toDateString()).getTime();
      return mTime < today;
    });
    const upcomingList = equipment.filter(eq => {
      const mTime = new Date(new Date(eq.next_maintenance_date).toDateString()).getTime();
      return mTime >= today && mTime <= in14days;
    }).sort((a, b) => new Date(a.next_maintenance_date) - new Date(b.next_maintenance_date));

    const renderTable = (list, isOverdue) => {
      let html = `<table class="overdue-table"><thead><tr>
        <th>${t('common.company') || 'Name'}</th>
        <th>${t('equipment.locationLabel') || 'Location'}</th>
        <th>${isOverdue ? (t('maintenance.daysOverdue') || 'Overdue') : 'Due in'}</th>
        <th>${t('maintenance.dueDate') || 'Due Date'}</th>
      </tr></thead><tbody>`

      list.forEach(eq => {
        const d = new Date(eq.next_maintenance_date)
        const mTime = new Date(d.toDateString()).getTime()
        const diffDays = Math.abs(Math.floor((today - mTime) / (1000 * 60 * 60 * 24)))
        const badgeClass = isOverdue
          ? 'overdue-days ' + (diffDays > 7 ? 'overdue-days-critical' : 'overdue-days-warning')
          : 'upcoming-days'
        const icon = isOverdue ? 'alert-triangle' : 'clock'
        html += `<tr onclick="window.handleOverdueClick('${eq.id}')" class="overdue-row">
          <td data-label="Name">
            <strong>${eq.name}</strong>
            <div style="font-size:0.85em;color:#64748b;">${eq.equipment_type?.name || ''}</div>
          </td>
          <td data-label="Location">${eq.location || '-'}</td>
          <td data-label="Status">
            <span class="${badgeClass}">
              <i data-lucide="${icon}"></i> ${diffDays}d
            </span>
          </td>
          <td data-label="Due Date">${d.toLocaleDateString()}</td>
        </tr>`
      })
      return html + '</tbody></table>'
    }

    if (overdueList.length === 0 && upcomingList.length === 0) {
      content.innerHTML = `
        <div class="overdue-empty">
          <div class="overdue-empty-icon"><i data-lucide="check-circle"></i></div>
          <h3>${t('overdue.allCaughtUp')}</h3>
          <p>${t('overdue.noOverdueMaintenance')}</p>
        </div>`;
      refreshIcons();
      return;
    }

    let html = ''
    if (overdueList.length > 0) {
      overdueList.sort((a, b) => new Date(a.next_maintenance_date) - new Date(b.next_maintenance_date))
      html += `<div class="overdue-section-title">⚠ Overdue (${overdueList.length})</div>`
      html += renderTable(overdueList, true)
    }
    if (upcomingList.length > 0) {
      html += `<div class="overdue-section-title">📅 Due in next 14 days (${upcomingList.length})</div>`
      html += renderTable(upcomingList, false)
    }

    content.innerHTML = html;
    refreshIcons();
    
  } catch (error) {
    console.error('Error in showOverdueModal:', error);
    content.innerHTML = `<p class="error">${error.message}</p>`;
  }
}

/**
 * Закрыть модальное окно просрочек
 */
function closeOverdueModal() {
  const modal = document.getElementById('overdueModal')
  if (modal) {
    modal.style.display = 'none'
  }
}

// ==================== EVENT LISTENERS ====================
// ==================== STAT CARDS CLICK HANDLERS ====================

// Функция переключения на таб
function switchToTab(tabName) {
  document.querySelectorAll('.tab-content').forEach(c => c.classList.remove('active'))
  document.getElementById(`${tabName}Tab`)?.classList.add('active')

  document.querySelectorAll('.dashboard-nav .tab-btn').forEach(b => b.classList.remove('active'))
  document.querySelector(`.dashboard-nav .tab-btn[data-tab="${tabName}"]`)?.classList.add('active')

  // The desktop card row is the same navigation in another shape
  document.querySelectorAll('.nav-card').forEach(c => c.classList.remove('active'))
  document.querySelector(`.nav-card[data-tab="${tabName}"]`)?.classList.add('active')

  if (tabName === 'checklists') loadChecklists()
  if (tabName === 'operations') window.loadOperationsTab()
  if (tabName === 'buildings') loadBuildings()
  if (tabName === 'tags') loadTags()

  // On mobile the nav lives in a drawer — picking a tab closes it.
  closeSidebar()
  refreshIcons()
}

// ==================== SIDEBAR (drawer on mobile) ====================

function openSidebar() {
  document.getElementById('appSidebar')?.classList.add('open')
  document.getElementById('sidebarOverlay')?.classList.add('open')
  document.body.classList.add('sidebar-open')
}

function closeSidebar() {
  document.getElementById('appSidebar')?.classList.remove('open')
  document.getElementById('sidebarOverlay')?.classList.remove('open')
  document.body.classList.remove('sidebar-open')
}

document.getElementById('sidebarToggle')?.addEventListener('click', () => {
  const isOpen = document.getElementById('appSidebar')?.classList.contains('open')
  isOpen ? closeSidebar() : openSidebar()
})

document.getElementById('sidebarOverlay')?.addEventListener('click', closeSidebar)

// ==================== SIDEBAR COUNTS ====================

// The stat cards are hidden on mobile, so their numbers move into the nav.
// Mirroring them from the cards keeps one source of truth: every loader that
// already updates a card updates the badge for free.
const NAV_COUNT_SOURCES = {
  navCountBuildings:   'totalBuildings',
  navCountEquipment:   'totalEquipment',
  navCountTags:        'availableTags',
  navCountSpecialists: 'totalSpecialists',
  navCountChecklists:  'totalChecklists',
  navCountOperations:  'upcomingCount',
}

function syncNavCounts() {
  Object.entries(NAV_COUNT_SOURCES).forEach(([badgeId, statId]) => {
    const badge = document.getElementById(badgeId)
    const value = document.getElementById(statId)?.textContent?.trim()
    if (!badge) return

    badge.textContent = value || ''
    // A zero count is noise on these rows — nothing is waiting on the admin.
    badge.style.display = (!value || value === '0') ? 'none' : ''
  })
}

// Watch the stat values instead of patching every loader that writes them.
function watchStatsForNav() {
  const observer = new MutationObserver(syncNavCounts)
  Object.values(NAV_COUNT_SOURCES).forEach(statId => {
    const el = document.getElementById(statId)
    if (el) observer.observe(el, { childList: true, characterData: true, subtree: true })
  })
  syncNavCounts()
}

watchStatsForNav()

document.addEventListener('keydown', (e) => {
  if (e.key === 'Escape') closeSidebar()
})

// Buildings tab wiring + refresh hook used by buildings.js after a save
initBuildings()
initHooldusraamat()
initTags()
window.loadEquipmentList = loadEquipmentList

/**
 * Jump from the Buildings tree to the equipment list, scoped to one
 * building (and optionally one zone inside it). The active-filter chips
 * show what the list is scoped to and let the admin drop it again.
 */
window.showEquipmentInLocation = function (buildingId, zoneId = null) {
  clearAllFilters()
  equipmentFilters.building = buildingId || ''
  equipmentFilters.zone = zoneId || ''
  syncLocationFilterSelects()
  switchToTab('equipment')
  renderFilteredEquipment()
}

/** Jump from a specialist row on the Specialists tab to their assigned equipment. */
window.showEquipmentForSpecialist = function (specialistId) {
  clearAllFilters()
  equipmentFilters.specialist = specialistId || ''
  const select = document.getElementById('filterSpecialist')
  if (select) select.value = equipmentFilters.specialist
  switchToTab('equipment')
  renderFilteredEquipment()
}

// ==================== MAINTENANCE TASK MODAL ====================
let _maintTaskEquipment = null

window.openMaintenanceTask = async function(equipmentId, diffDays, isOverdue) {
  const modal = document.getElementById('maintenanceTaskModal')
  if (!modal) return

  // Load specialists if not cached
  if (!srSpecialistsCache) {
    const { data: specs } = await supabase
      .from('users')
      .select('id, full_name, email')
      .eq('tenant_id', currentUser.tenant_id)
      .order('full_name')
    srSpecialistsCache = specs || []
  }

  // Find equipment data from the already-loaded operations feed data
  const { data: eq } = await supabase
    .from('equipment')
    .select('id, name, location, client_company')
    .eq('id', equipmentId)
    .single()

  _maintTaskEquipment = eq

  // Update header
  const titleEl = document.getElementById('maintTaskTitle')
  const metaEl = document.getElementById('maintTaskMeta')
  if (titleEl) titleEl.textContent = eq?.name || t('maintenance.createTaskTitle')
  if (metaEl) {
    const dayLabel = `${diffDays} ${t('maintenance.days')}`
    metaEl.textContent = isOverdue
      ? `${eq?.location || ''} · ${t('maintenance.overdueBy')} ${dayLabel}`
      : `${eq?.location || ''} · ${t('maintenance.upcomingIn')} ${dayLabel}`
  }

  // Populate specialists dropdown
  const select = document.getElementById('maintTaskSpecialist')
  if (select) {
    select.innerHTML = `<option value="">${t('serviceRequests.noSpecialist')}</option>`
    srSpecialistsCache.forEach(s => {
      const opt = document.createElement('option')
      opt.value = s.id
      opt.textContent = s.full_name
      select.appendChild(opt)
    })
  }

  // Reset form fields
  const notesEl = document.getElementById('maintTaskNotes')
  const priorityEl = document.getElementById('maintTaskPriority')
  if (notesEl) notesEl.value = ''
  if (priorityEl) priorityEl.value = 'medium'

  modal.style.display = 'flex'
  updateTranslations()
}

window.closeMaintenanceTask = function() {
  const modal = document.getElementById('maintenanceTaskModal')
  if (modal) modal.style.display = 'none'
  _maintTaskEquipment = null
}

window.submitMaintenanceTask = async function() {
  const eq = _maintTaskEquipment
  if (!eq) return

  const specialistId = document.getElementById('maintTaskSpecialist')?.value || null
  const priority = document.getElementById('maintTaskPriority')?.value || 'medium'
  const notes = document.getElementById('maintTaskNotes')?.value?.trim() || ''

  const description = notes
    ? `[${t('mechanicPage.maintenance')}] ${notes}`
    : `[${t('mechanicPage.maintenance')}]`

  const specialist = specialistId ? srSpecialistsCache.find(s => s.id === specialistId) : null
  const displayCompany = eq.client_company || currentUser.tenant?.company_name || '—'

  const btn = document.querySelector('#maintenanceTaskModal .btn-primary')
  if (btn) btn.disabled = true

  try {
    const { data: inserted, error } = await supabase
      .from('service_requests')
      .insert([{
        tenant_id: currentUser.tenant_id,
        equipment_id: eq.id,
        equipment_name: eq.name,
        equipment_location: eq.location || '',
        client_company: eq.client_company || null,
        requester_name: currentUser.full_name || currentUser.username,
        requester_contact: '',
        problem_description: description,
        status: specialistId ? 'assigned' : 'new',
        priority,
        assigned_to: specialistId || null,
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }])
      .select()
      .single()

    if (error) throw error

    // Send email notification (fire-and-forget)
    if (specialist?.email) {
      supabase.functions.invoke('send-service-request-email', {
        body: {
          equipment_name: eq.name || '—',
          equipment_location: eq.location || '—',
          company_name: displayCompany,
          admin_email: specialist.email,
          specialist_email: null,
          specialist_name: specialist.full_name,
          priority,
          requester_name: currentUser.full_name || currentUser.username,
          requester_contact: '',
          problem_description: description,
          photos: []
        }
      }).catch(e => console.warn('Email notification failed:', e))
    }

    if (specialistId) {
      supabase.functions.invoke('send-telegram-notification', {
        body: {
          user_ids: [specialistId],
          text: `📋 *Task assigned to you*\n\n${eq.name || '—'} — ${displayCompany}\nLocation: ${eq.location || '—'}\n\n${description}`
        }
      }).catch(e => console.warn('Telegram notification failed:', e))
    }

    await loadServiceRequestsCount()
    window.closeMaintenanceTask()
    window.loadOperationsTab()
    alert(specialist ? t('maintenance.taskCreatedAssigned') : t('maintenance.taskCreated'))

  } catch (err) {
    console.error('Error creating maintenance task:', err)
    alert(t('error.prefix') + err.message)
  } finally {
    if (btn) btn.disabled = false
  }
}

// ==================== OPERATIONS TAB ====================
window.openServiceRequestsModal = function() {
  document.getElementById('serviceRequestsModal').style.display = 'flex'
  loadServiceRequestsModal()
}

window.openSingleRequest = async function(requestId) {
  const modal = document.getElementById('singleRequestModal')
  const content = document.getElementById('singleRequestContent')
  const title = document.getElementById('srDetailTitle')
  const meta = document.getElementById('srDetailMeta')
  if (!modal || !content) return

  modal.style.display = 'flex'
  content.innerHTML = `<p class="loading">Loading...</p>`

  try {
    // Ensure specialists are cached
    if (!srSpecialistsCache) {
      const { data: specs } = await supabase
        .from('users')
        .select('id, full_name, email')
        .eq('tenant_id', currentUser.tenant_id)
        .order('full_name')
      srSpecialistsCache = specs || []
    }

    const { data: req, error } = await supabase
      .from('service_requests')
      .select('*')
      .eq('id', requestId)
      .single()
    if (error) throw error

    // Store in map for actions
    srRequestsMap[req.id] = req

    title.textContent = srEscapeHtml(req.equipment_name || 'Request')
    const ageMs = Date.now() - new Date(req.created_at).getTime()
    const ageDays = Math.floor(ageMs / (1000 * 60 * 60 * 24))
    const ageHours = Math.floor(ageMs / (1000 * 60 * 60))
    const ageLabel = ageDays >= 1 ? `${ageDays}d ago` : `${ageHours}h ago`
    const isStale = req.status !== 'done' && ageMs > 3 * 24 * 60 * 60 * 1000
    meta.textContent = `${req.equipment_location || ''} · Created ${ageLabel}`

    const priority = req.priority || 'medium'
    const priorityLabel = t(`serviceRequests.priority${priority.charAt(0).toUpperCase() + priority.slice(1)}`)
    let statusLabel = t('serviceRequests.statusNew')
    let statusClass = 'status-new'
    if (req.status === 'assigned') { statusLabel = t('serviceRequests.statusAssigned'); statusClass = 'status-assigned' }
    if (req.status === 'in_progress') { statusLabel = t('serviceRequests.statusInProgress'); statusClass = 'status-inprogress' }
    if (req.status === 'done') { statusLabel = t('serviceRequests.statusDone'); statusClass = 'status-done' }

    const assignedUser = req.assigned_to ? srSpecialistsCache.find(s => s.id === req.assigned_to) : null

    let html = `<div class="sr-detail-badges">
      <span class="badge-priority priority-${priority}">${priorityLabel}</span>
      <span class="badge ${statusClass}">${statusLabel}</span>
      ${isStale ? `<span class="stale-badge">⚠ stale ${ageDays}d</span>` : `<span class="age-badge">${ageLabel}</span>`}
    </div>`

    // Problem description
    html += `<div class="sr-detail-problem">${srEscapeHtml(req.problem_description || '—')}</div>`

    // Requester
    html += `<div class="sr-detail-section">
      <div class="sr-detail-label">Requester</div>
      <div class="sr-detail-value">
        ${srEscapeHtml(req.requester_name || '—')}
        ${req.requester_contact ? ` · <a class="sr-detail-contact" href="tel:${srEscapeHtml(req.requester_contact)}">${srEscapeHtml(req.requester_contact)}</a>` : ''}
      </div>
    </div>`

    // Assigned specialist
    if (assignedUser) {
      html += `<div class="sr-detail-section">
        <div class="sr-detail-label">Assigned to</div>
        <div class="sr-detail-value">${srEscapeHtml(assignedUser.full_name)}</div>
      </div>`
    }

    // Resolved info
    if (req.status === 'done' && req.resolved_at) {
      html += `<div class="sr-detail-section">
        <div class="sr-detail-label">Resolved</div>
        <div class="sr-detail-value">${new Date(req.resolved_at).toLocaleDateString()}</div>
      </div>`
    }

    // Photos
    const photos = Array.isArray(req.photos) ? req.photos : []
    if (photos.length > 0) {
      html += `<div class="sr-detail-label" style="margin-bottom:6px;">Photos</div>
        <div class="sr-detail-photos">`
      photos.forEach((src, i) => {
        html += `<img class="sr-detail-photo" src="${src}" alt="Photo ${i + 1}" onclick="window.open(this.src,'_blank')">`
      })
      html += `</div>`
    }

    // Actions (only if not done)
    if (req.status !== 'done') {
      const specialistOptions = srSpecialistsCache.map(s =>
        `<option value="${s.id}" ${req.assigned_to === s.id ? 'selected' : ''}>${srEscapeHtml(s.full_name)}</option>`
      ).join('')

      html += `<div class="sr-detail-actions">
        <select onchange="window.srDetailSetPriority('${req.id}', this.value)" title="Priority">
          <option value="high" ${priority === 'high' ? 'selected' : ''}>${t('serviceRequests.priorityHigh')}</option>
          <option value="medium" ${priority === 'medium' ? 'selected' : ''}>${t('serviceRequests.priorityMedium')}</option>
          <option value="low" ${priority === 'low' ? 'selected' : ''}>${t('serviceRequests.priorityLow')}</option>
        </select>
        <select id="sr-detail-assign-${req.id}">
          <option value="">${t('serviceRequests.noSpecialist')}</option>
          ${specialistOptions}
        </select>
        <button class="btn btn-primary btn-sm" onclick="window.srDetailAssign('${req.id}')">${t('serviceRequests.assignAndNotify')}</button>
        <button class="btn btn-secondary btn-sm" onclick="window.srDetailMarkDone('${req.id}')">${t('serviceRequests.markDone')}</button>
      </div>`
    }

    content.innerHTML = html
    refreshIcons()

  } catch (err) {
    console.error('Error loading request detail:', err)
    content.innerHTML = `<p style="color:#ef4444;padding:1rem;">Error: ${err.message}</p>`
  }
}

window.closeSingleRequest = function() {
  const modal = document.getElementById('singleRequestModal')
  if (modal) modal.style.display = 'none'
}

window.srDetailSetPriority = async function(requestId, priority) {
  try {
    const { error } = await supabase
      .from('service_requests')
      .update({ priority, updated_at: new Date().toISOString() })
      .eq('id', requestId)
    if (error) throw error
    if (srRequestsMap[requestId]) srRequestsMap[requestId].priority = priority
    window.openSingleRequest(requestId)
  } catch (err) {
    alert(t('error.prefix') + err.message)
  }
}

window.srDetailAssign = async function(requestId) {
  const select = document.getElementById(`sr-detail-assign-${requestId}`)
  if (!select) return
  const specialistId = select.value
  const req = srRequestsMap[requestId]
  if (!req) return

  try {
    const specialist = srSpecialistsCache.find(s => s.id === specialistId)
    const updateData = {
      assigned_to: specialistId || null,
      status: specialistId ? 'assigned' : req.status,
      updated_at: new Date().toISOString()
    }
    const { error } = await supabase
      .from('service_requests')
      .update(updateData)
      .eq('id', requestId)
    if (error) throw error

    const displayCompany = req.client_company || currentUser.tenant?.company_name || '—'

    // Send email notification — don't block assignment if it fails
    if (specialistId && specialist && specialist.email) {
      supabase.functions.invoke('send-service-request-email', {
        body: {
          equipment_name: req.equipment_name || '—',
          equipment_location: req.equipment_location || '—',
          company_name: displayCompany,
          admin_email: specialist.email,
          specialist_email: null,
          specialist_name: specialist.full_name,
          priority: req.priority || 'medium',
          requester_name: req.requester_name,
          requester_contact: req.requester_contact || '—',
          problem_description: req.problem_description,
          photos: []
        }
      }).catch(e => console.warn('Email notification failed:', e))
    }

    if (specialistId) {
      supabase.functions.invoke('send-telegram-notification', {
        body: {
          user_ids: [specialistId],
          text: `📋 *Task assigned to you*\n\n${req.equipment_name || '—'} — ${displayCompany}\nLocation: ${req.equipment_location || '—'}\n\n${req.problem_description || ''}`
        }
      }).catch(e => console.warn('Telegram notification failed:', e))
    }

    await loadServiceRequestsCount()
    alert(t('alert.requestAssigned'))
    window.openSingleRequest(requestId)
  } catch (err) {
    alert(t('error.prefix') + err.message)
  }
}

window.srDetailMarkDone = async function(requestId) {
  try {
    const { error } = await supabase
      .from('service_requests')
      .update({ status: 'done', resolved_at: new Date().toISOString(), updated_at: new Date().toISOString() })
      .eq('id', requestId)
    if (error) throw error
    await loadServiceRequestsCount()
    window.closeSingleRequest()
    window.loadOperationsTab()
  } catch (err) {
    alert(t('error.prefix') + err.message)
  }
}

window.loadOperationsTab = async function() {
  const feed = document.getElementById('operationsFeed')
  if (!feed) return
  feed.innerHTML = '<p class="loading">Loading...</p>'

  try {
    const now = new Date()
    const today = new Date(now.getFullYear(), now.getMonth(), now.getDate()).getTime()
    const in14days = today + 14 * 24 * 60 * 60 * 1000

    const { data: equipment } = await supabase.from('equipment')
      .select('id, name, location, next_maintenance_date, equipment_type:equipment_types(name)')
      .eq('tenant_id', currentUser.tenant_id)
      .eq('status', 'active')
      .not('next_maintenance_date', 'is', null)

    // Maintenance falling due within the next 14 days. Overdue work and open
    // service requests each have their own entry point in the info strip.
    const items = []

    ;(equipment || []).forEach(eq => {
      const mTime = new Date(new Date(eq.next_maintenance_date).toDateString()).getTime()
      if (mTime >= today && mTime <= in14days) {
        const diffDays = Math.floor((mTime - today) / (1000 * 60 * 60 * 24))
        items.push({ type: 'upcoming', urgency: diffDays, diffDays, eq })
      }
    })

    // Soonest first
    items.sort((a, b) => a.urgency - b.urgency)

    if (items.length === 0) {
      feed.innerHTML = `<div class="ops-empty">
        <i data-lucide="check-circle" style="width:40px;height:40px;color:#10b981;"></i>
        <p style="font-size:15px;font-weight:600;color:#1e293b;margin:0 0 4px;">${t('attention.emptyTitle')}</p>
        <p style="font-size:13px;color:#94a3b8;margin:0;">${t('attention.emptyHint')}</p>
      </div>`
      refreshIcons()

      // Hide badge
      const badge = document.getElementById('opsBadge')
      if (badge) badge.style.display = 'none'
      return
    }

    const badge = document.getElementById('opsBadge')
    if (badge) {
      badge.textContent = items.length
      badge.style.display = 'inline-block'
    }

    let html = ''
    items.forEach(item => {
      html += `<div class="ops-item ops-upcoming" onclick="window.openMaintenanceTask('${item.eq.id}', ${item.diffDays}, false)">
        <div class="ops-icon"><i data-lucide="clock"></i></div>
        <div class="ops-body">
          <div class="ops-title">${srEscapeHtml(item.eq.name)}</div>
          <div class="ops-meta">
            <span class="ops-meta-item"><i data-lucide="map-pin"></i> ${srEscapeHtml(item.eq.location || '—')}</span>
            <span class="ops-meta-item" style="color:#94a3b8;">${srEscapeHtml(item.eq.equipment_type?.name || '')}</span>
          </div>
        </div>
        <div class="ops-right">
          <span class="ops-days">${t('maintenance.upcomingIn')} ${item.diffDays}${t('maintenance.days')}</span>
        </div>
      </div>`
    })

    feed.innerHTML = html
    refreshIcons()

  } catch (err) {
    console.error('Operations tab error:', err)
    feed.innerHTML = `<p style="color:#ef4444;padding:1rem;">Error: ${err.message}</p>`
  }
}

// Клик на карточку Equipment
// The card row is the primary navigation on desktop, so one delegated
// handler drives every card that maps to a tab.
document.querySelector('.nav-cards')?.addEventListener('click', (e) => {
  const card = e.target.closest('.nav-card[data-tab]')
  if (card) switchToTab(card.dataset.tab)
})

// Tags are a modal rather than a tab
// Overdue modal (still accessible from Operations tab items)
document.getElementById('closeOverdueModal')?.addEventListener('click', closeOverdueModal)
document.getElementById('closeOverdueBtn')?.addEventListener('click', closeOverdueModal)
document.querySelector('#overdueModal .modal-overlay')?.addEventListener('click', closeOverdueModal)

// Service requests modal close handlers


// The info-strip blocks are the direct route to what they count: overdue
// maintenance and open service requests each open their own list, instead of
// both hiding behind a card called "Operations".
document.getElementById('overdueCard')?.addEventListener('click', showOverdueModal)
document.getElementById('serviceRequestsCard')?.addEventListener('click', () => {
  window.openServiceRequestsModal()
})
// Закрыть модалку service requests
document.getElementById('closeServiceRequestsModal')?.addEventListener('click', () => {
  document.getElementById('serviceRequestsModal').style.display = 'none'
})
document.getElementById('closeServiceRequestsBtn')?.addEventListener('click', () => {
  document.getElementById('serviceRequestsModal').style.display = 'none'
})
document.querySelector('#serviceRequestsModal .modal-overlay')?.addEventListener('click', () => {
  document.getElementById('serviceRequestsModal').style.display = 'none'
})



// ============================================
// BILLING REPORT
// ============================================

const billingReportModal = document.getElementById('billingReportModal')

document.getElementById('billingReportBtn')?.addEventListener('click', () => {
  // Заполняем список клиентов
  const clientSelect = document.getElementById('billingReportClient')
  if (clientSelect) {
    const clients = [...new Set(allEquipment.map(e => e.client_company).filter(Boolean))].sort()
    clientSelect.innerHTML = `<option value="">— Select client —</option>`
    clients.forEach(c => {
      const opt = document.createElement('option')
      opt.value = c
      opt.textContent = c
      clientSelect.appendChild(opt)
    })
  }
  // Дефолтный диапазон: последние 30 дней
  const today = new Date()
  const monthAgo = new Date(today)
  monthAgo.setDate(today.getDate() - 30)
  const fmt = d => d.toISOString().substring(0, 10)
  const dfrom = document.getElementById('billingReportDateFrom')
  const dto = document.getElementById('billingReportDateTo')
  if (dfrom) dfrom.value = fmt(monthAgo)
  if (dto) dto.value = fmt(today)

  document.getElementById('billingReportStatus').textContent = ''
  document.getElementById('billingReportTable').innerHTML = ''
  document.getElementById('exportBillingReportBtn').style.display = 'none'
  billingReportData = []

  billingReportModal.style.display = 'flex'
  refreshIcons()
})

document.getElementById('closeBillingReportModal')?.addEventListener('click', () => {
  billingReportModal.style.display = 'none'
})
document.getElementById('cancelBillingReportBtn')?.addEventListener('click', () => {
  billingReportModal.style.display = 'none'
})
billingReportModal?.querySelector('.modal-overlay')?.addEventListener('click', () => {
  billingReportModal.style.display = 'none'
})

document.getElementById('generateBillingReportBtn')?.addEventListener('click', async () => {
  const client = document.getElementById('billingReportClient').value
  const dateFrom = document.getElementById('billingReportDateFrom').value
  const dateTo = document.getElementById('billingReportDateTo').value
  const statusEl = document.getElementById('billingReportStatus')
  const tableEl = document.getElementById('billingReportTable')
  const exportBtn = document.getElementById('exportBillingReportBtn')

  if (!client) {
    statusEl.textContent = t('billingReport.errorNoClient')
    return
  }
  if (!dateFrom || !dateTo) {
    statusEl.textContent = t('billingReport.errorNoDate')
    return
  }

  statusEl.textContent = t('billingReport.loading')
  tableEl.innerHTML = ''
  exportBtn.style.display = 'none'
  billingReportData = []

  try {
    // Получаем ID оборудования для выбранного клиента
    const clientEquipmentIds = allEquipment
      .filter(e => e.client_company === client)
      .map(e => e.id)

    if (clientEquipmentIds.length === 0) {
      statusEl.textContent = t('billingReport.noEquipment')
      return
    }

    const { data: logs, error } = await supabase
      .from('service_history')
      .select('*, mechanic:users(full_name)')
      .in('equipment_id', clientEquipmentIds)
      .gte('work_date', dateFrom)
      .lte('work_date', dateTo)
      .order('work_date', { ascending: false })

    if (error) throw error

    if (!logs || logs.length === 0) {
      statusEl.textContent = t('billingReport.noRecords', { client, from: dateFrom, to: dateTo })
      return
    }

    // Обогащаем данные: добавляем имя оборудования
    const equipmentMap = Object.fromEntries(allEquipment.map(e => [e.id, e]))
    billingReportData = logs.map(log => {
      const items = Array.isArray(log.work_items) ? log.work_items : []
      const totalHours = items.reduce((sum, item) => sum + (parseFloat(item.hours) || 0), 0)
      return {
        ...log,
        equipment_name: equipmentMap[log.equipment_id]?.name || '—',
        equipment_serial: equipmentMap[log.equipment_id]?.serial_number || '—',
        total_hours: totalHours || null,
      }
    })

    statusEl.textContent = t('billingReport.found', { count: billingReportData.length, client })

    // Рендерим таблицу
    let html = `<table style="width:100%; border-collapse:collapse; font-size:13px;">
      <thead>
        <tr style="background:#f1f5f9; text-align:left;">
          <th style="padding:8px; border-bottom:2px solid #e2e8f0;">Date</th>
          <th style="padding:8px; border-bottom:2px solid #e2e8f0;">Equipment</th>
          <th style="padding:8px; border-bottom:2px solid #e2e8f0;">Serial</th>
          <th style="padding:8px; border-bottom:2px solid #e2e8f0;">Work Type</th>
          <th style="padding:8px; border-bottom:2px solid #e2e8f0;">Description</th>
          <th style="padding:8px; border-bottom:2px solid #e2e8f0; text-align:right;">Hours</th>
          <th style="padding:8px; border-bottom:2px solid #e2e8f0;">Specialist</th>
        </tr>
      </thead>
      <tbody>`

    let grandTotal = 0
    billingReportData.forEach(log => {
      const date = log.work_date ? new Date(log.work_date).toLocaleDateString() : '—'
      const desc = log.description ? log.description.replace(/</g, '&lt;').replace(/>/g, '&gt;') : '—'
      const hours = log.total_hours != null ? log.total_hours : '—'
      if (log.total_hours) grandTotal += log.total_hours
      html += `<tr style="border-bottom:1px solid #e2e8f0;">
        <td style="padding:7px 8px; white-space:nowrap;">${date}</td>
        <td style="padding:7px 8px;">${log.equipment_name}</td>
        <td style="padding:7px 8px; color:#64748b;">${log.equipment_serial}</td>
        <td style="padding:7px 8px; text-transform:capitalize;">${log.work_type || '—'}</td>
        <td style="padding:7px 8px; max-width:280px;">${desc}</td>
        <td style="padding:7px 8px; text-align:right; font-weight:500;">${hours}</td>
        <td style="padding:7px 8px;">${log.mechanic?.full_name || '—'}</td>
      </tr>`
    })

    if (grandTotal > 0) {
      html += `<tr style="background:#f8fafc; font-weight:bold; border-top:2px solid #cbd5e1;">
        <td colspan="5" style="padding:8px; text-align:right;">Total hours:</td>
        <td style="padding:8px; text-align:right;">${grandTotal % 1 === 0 ? grandTotal : grandTotal.toFixed(1)}</td>
        <td></td>
      </tr>`
    }

    html += `</tbody></table>`
    tableEl.innerHTML = html

    exportBtn.style.display = 'inline-flex'

  } catch (err) {
    console.error('Billing report error:', err)
    statusEl.textContent = `Error: ${err.message}`
  }
})

document.getElementById('exportBillingReportBtn')?.addEventListener('click', async () => {
  if (!billingReportData.length) return

  const client = document.getElementById('billingReportClient').value
  const dateFrom = document.getElementById('billingReportDateFrom').value
  const dateTo = document.getElementById('billingReportDateTo').value

  const workbook = new ExcelJS.Workbook()
  const ws = workbook.addWorksheet('Billing Report')

  ws.columns = [
    { header: 'Date', key: 'date', width: 14 },
    { header: 'Equipment', key: 'equipment', width: 28 },
    { header: 'Serial Number', key: 'serial', width: 18 },
    { header: 'Work Type', key: 'work_type', width: 16 },
    { header: 'Description', key: 'description', width: 40 },
    { header: 'Hours', key: 'hours', width: 10 },
    { header: 'Specialist', key: 'specialist', width: 22 },
  ]

  let excelTotal = 0
  billingReportData.forEach(log => {
    if (log.total_hours) excelTotal += log.total_hours
    ws.addRow({
      date: log.work_date ? new Date(log.work_date).toLocaleDateString() : '—',
      equipment: log.equipment_name,
      serial: log.equipment_serial,
      work_type: log.work_type || '—',
      description: log.description || '—',
      hours: log.total_hours != null ? log.total_hours : '',
      specialist: log.mechanic?.full_name || '—',
    })
  })

  if (excelTotal > 0) {
    const totalRow = ws.addRow({
      date: '',
      equipment: '',
      serial: '',
      work_type: '',
      description: 'Total hours:',
      hours: parseFloat(excelTotal.toFixed(1)),
      specialist: '',
    })
    totalRow.font = { bold: true }
    totalRow.getCell('description').alignment = { horizontal: 'right' }
  }

  ws.getRow(1).font = { bold: true }
  ws.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FFE7E6E6' } }
  ws.views = [{ state: 'frozen', ySplit: 1 }]
  ws.autoFilter = { from: { row: 1, column: 1 }, to: { row: 1, column: ws.columns.length } }

  const filename = `billing_${client.replace(/\s+/g, '_')}_${dateFrom}_${dateTo}.xlsx`
  const buffer = await workbook.xlsx.writeBuffer()
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  a.click()
  URL.revokeObjectURL(url)
})

// ============================================
// EQUIPMENT FILTERS
// ============================================
export function getFilteredEquipment() {
  return allEquipment.filter(item => {
    if (equipmentFilters.search) {
      const s = equipmentFilters.search.toLowerCase()
      const cfValues = Object.values(item.custom_fields || {}).join(' ').toLowerCase()
      if (!item.name?.toLowerCase().includes(s) &&
          !item.serial_number?.toLowerCase().includes(s) &&
          !item.location?.toLowerCase().includes(s) &&
          !item.client_company?.toLowerCase().includes(s) &&
          !item.nfc_id?.toLowerCase().includes(s) &&
          !item.ai_scan_data?.toLowerCase().includes(s) &&
          !cfValues.includes(s)) return false
    }
    if (equipmentFilters.client && item.client_company !== equipmentFilters.client) return false
    if (equipmentFilters.status && item.status !== equipmentFilters.status) return false
    if (equipmentFilters.type && item.equipment_type?.code !== equipmentFilters.type) return false
    if (equipmentFilters.specialist && item.assigned_specialist_id !== equipmentFilters.specialist) return false
    if (equipmentFilters.noNfc && item.nfc_id) return false
    if (equipmentFilters.building && item.building_id !== equipmentFilters.building) return false
    if (equipmentFilters.zone && item.zone_id !== equipmentFilters.zone) return false
    return true
  })
}
// Рендерим отфильтрованное оборудование
function renderFilteredEquipment() {
  const container = document.getElementById('equipmentList')
  
  if (!allEquipment || allEquipment.length === 0) {
    container.innerHTML = `<p class="loading">${t('equipment.noEquipmentYet')}</p>`
    updateEquipmentCounter(0, 0)
    return
  }
  
  // Применяем все фильтры
  let filtered = allEquipment.filter(item => {
    // Фильтр по поиску
    if (equipmentFilters.search) {
      const searchLower = equipmentFilters.search.toLowerCase()
      const cfValues = Object.values(item.custom_fields || {}).join(' ').toLowerCase()
      const matchName = item.name?.toLowerCase().includes(searchLower)
      const matchSerial = item.serial_number?.toLowerCase().includes(searchLower)
      const matchLocation = item.location?.toLowerCase().includes(searchLower)
      const matchClient = item.client_company?.toLowerCase().includes(searchLower)
      const matchNfc = item.nfc_id?.toLowerCase().includes(searchLower)
      const matchAI = item.ai_scan_data?.toLowerCase().includes(searchLower)
      const matchCF = cfValues.includes(searchLower)

      if (!matchName && !matchSerial && !matchLocation && !matchClient && !matchNfc && !matchAI && !matchCF) {
        return false
      }
    }

    // Фильтр по клиенту
    if (equipmentFilters.client && item.client_company !== equipmentFilters.client) {
      return false
    }

    // Фильтр по статусу
    if (equipmentFilters.status && item.status !== equipmentFilters.status) {
      return false
    }

    // Фильтр по типу
    if (equipmentFilters.type && item.equipment_type?.code !== equipmentFilters.type) {
      return false
    }

    // Фильтр по специалисту
    if (equipmentFilters.specialist && item.assigned_specialist_id !== equipmentFilters.specialist) {
      return false
    }

    // Фильтр: только без NFC
    if (equipmentFilters.noNfc && item.nfc_id) {
      return false
    }

    // Location filter, set by clicking a building/zone in the Buildings tab
    if (equipmentFilters.building && item.building_id !== equipmentFilters.building) {
      return false
    }
    if (equipmentFilters.zone && item.zone_id !== equipmentFilters.zone) {
      return false
    }

    return true
  })
  
  // Сортировка
  if (equipmentFilters.sort === 'next_maintenance_asc') {
    filtered.sort((a, b) => {
      const da = a.next_maintenance_date ? new Date(a.next_maintenance_date) : null
      const db = b.next_maintenance_date ? new Date(b.next_maintenance_date) : null
      if (!da && !db) return 0
      if (!da) return 1
      if (!db) return -1
      return da - db
    })
  } else if (equipmentFilters.sort === 'name_asc') {
    filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  }

  // Обновляем счётчик
  updateEquipmentCounter(filtered.length, allEquipment.length)

  if (filtered.length === 0) {
    container.innerHTML = `<p class="loading">${t('equipment.noResults')}</p>`
    return
  }

  // Рендерим equipment grid
  let html = '<div class="equipment-grid">'
  
  filtered.forEach(item => {
    const lucideIcon = getLucideIcon(item.equipment_type?.code)
    const typeName = item.equipment_type?.name || 'Equipment'
    
    const lastM = item.last_maintenance_date 
      ? new Date(item.last_maintenance_date).toLocaleDateString() 
      : '—'
    const nextM = item.next_maintenance_date 
      ? new Date(item.next_maintenance_date).toLocaleDateString() 
      : 'Not set'

    const isOverdue = item.next_maintenance_date && new Date(item.next_maintenance_date) < new Date().setHours(0,0,0,0)
    const dateClass = isOverdue ? 'date-overdue' : ''
    const maintPill = maintenancePillHtml(item)

    html += `
      <div class="equipment-card" onclick="openEquipmentDetail('${item.id}')">
        <div class="equipment-card-header">
          <div class="equipment-icon"><i data-lucide="${lucideIcon}"></i></div>
          <div>
            <div class="equipment-name">${item.name}</div>
            <small>${typeName}</small>
            ${locationBreadcrumbHtml(item)}
          </div>
        </div>
        <div class="equipment-details">
          ${item.client_company ? `<div><strong>${t('equipment.clientCompanyLabel')}:</strong> ${item.client_company}</div>` : ''}
          ${item.serial_number ? `<div><strong>${t('equipment.serialLabel')}:</strong> ${item.serial_number}</div>` : ''}
          ${item.location ? `<div><strong>${t('equipment.locationLabel')}:</strong> ${item.location}</div>` : ''}
          ${item.nfc_id ? `<div><strong>${t('equipment.nfcTagLabel')}:</strong> ${item.nfc_id}</div>` : ''}
		  ${item.checklist?.name ? `<div><strong><i data-lucide="clipboard-check"></i> Checklist:</strong> ${item.checklist.name}</div>` : ''}
          <div class="maintenance-block" style="margin-top: 10px; padding-top: 5px; border-top: 1px solid #eee;">
            ${maintPill ? `<div style="margin-bottom:6px;">${maintPill}</div>` : ''}
            <div>
              <strong>${t('equipment.maintenanceDate')}:</strong> ${lastM}
            </div>
            <div class="${dateClass}">
              <strong>${t('equipment.nextMaintenanceDate')}:</strong> ${nextM}
            </div>
            ${['industrial', 'vehicles'].includes(item.equipment_type?.code) && item.engine_hours != null ? `
            <div>
              <strong>${t('equipment.engine_hours')}:</strong> ${item.engine_hours} h
            </div>` : ''}
          </div>
        </div>
      </div>
    `
  })
  
  html += '</div>'
  container.innerHTML = html
  refreshIcons()
}

// Обновляем счётчик результатов
function updateEquipmentCounter(shown, total) {
  const counter = document.getElementById('equipmentCounter')
  if (!counter) return

  if (shown === total) {
    counter.textContent = t('equipment.showingAll').replace('{total}', total)
  } else {
    counter.textContent = t('equipment.showingFiltered')
      .replace('{shown}', shown)
      .replace('{total}', total)
  }

  // The toolbar badge and the chip row both describe the same filter state
  renderFilterChips()

  const applyText = document.getElementById('applyFiltersText')
  if (applyText) applyText.textContent = t('equipment.showResultsCount').replace('{count}', shown)
}

// ==================== FILTER CHIPS & COUNT ====================

/**
 * Describe every active filter as {key, label}. Drives both the chip row
 * and the count badge on the Filters button, so the two can never disagree.
 */
function activeFilterDescriptors() {
  const chips = []
  const { search, type, noNfc, building, zone, client, status, specialist, sort } = equipmentFilters

  if (search) chips.push({ key: 'search', label: `"${search}"` })

  if (type) {
    const btn = document.querySelector(`.type-filter-btn[data-type="${type}"] span`)
    chips.push({ key: 'type', label: btn?.textContent || type })
  }
  if (noNfc) chips.push({ key: 'noNfc', label: t('equipment.filterNoNfc') })

  if (building) {
    chips.push({ key: 'building', label: buildingsCache.find(b => b.id === building)?.name || '?' })
  }
  if (zone) {
    chips.push({ key: 'zone', label: zonesCache.find(z => z.id === zone)?.name || '?' })
  }

  if (client) chips.push({ key: 'client', label: client })

  if (status) {
    const opt = document.querySelector(`#filterStatus option[value="${status}"]`)
    chips.push({ key: 'status', label: opt?.textContent?.replace('●', '').trim() || status })
  }

  if (specialist) {
    const opt = document.querySelector(`#filterSpecialist option[value="${specialist}"]`)
    chips.push({ key: 'specialist', label: opt?.textContent || specialist })
  }

  if (sort) {
    const opt = document.querySelector(`#filterSort option[value="${sort}"]`)
    chips.push({ key: 'sort', label: opt?.textContent || sort })
  }

  return chips
}

function renderFilterChips() {
  const chipsEl = document.getElementById('filterChips')
  const badge = document.getElementById('activeFilterCount')
  const chips = activeFilterDescriptors()

  if (badge) {
    badge.textContent = chips.length
    badge.style.display = chips.length > 0 ? '' : 'none'
  }

  if (!chipsEl) return

  chipsEl.innerHTML = chips.map(chip => `
    <button type="button" class="filter-chip" data-filter-key="${chip.key}"
            title="${escapeHtml(t('equipment.removeFilter'))}">
      ${escapeHtml(chip.label)}<i data-lucide="x"></i>
    </button>`).join('')

  refreshIcons()
}

/** Clear one filter from the chip row and re-render. */
function clearOneFilter(key) {
  if (key === 'search') {
    equipmentFilters.search = ''
    const input = document.getElementById('equipmentSearchInput')
    if (input) input.value = ''
  } else if (key === 'type' || key === 'noNfc') {
    equipmentFilters.type = ''
    equipmentFilters.noNfc = false
    syncTypeButtons()
  } else if (key === 'building') {
    // A zone only means something inside its building
    equipmentFilters.building = ''
    equipmentFilters.zone = ''
    syncLocationFilterSelects()
  } else if (key === 'zone') {
    equipmentFilters.zone = ''
    syncLocationFilterSelects()
  } else {
    equipmentFilters[key] = ''
    const selectId = { client: 'filterClient', status: 'filterStatus', specialist: 'filterSpecialist', sort: 'filterSort' }[key]
    const select = selectId && document.getElementById(selectId)
    if (select) select.value = ''
  }

  renderFilteredEquipment()
}

/** Reflect equipmentFilters.type / .noNfc back onto the type buttons. */
function syncTypeButtons() {
  document.querySelectorAll('.type-filter-btn').forEach(btn => {
    const isNoNfc = btn.id === 'filterNoNfcBtn'
    const active = isNoNfc
      ? equipmentFilters.noNfc
      : !equipmentFilters.noNfc && btn.dataset.type === equipmentFilters.type
    btn.classList.toggle('active', active)
  })
}

// ==================== BUILDING / ZONE FILTER ====================

/** Fill the building and zone selects from the cache and current filter state. */
function syncLocationFilterSelects() {
  const buildingSelect = document.getElementById('filterBuilding')
  const zoneSelect = document.getElementById('filterZone')
  if (!buildingSelect || !zoneSelect) return

  buildingSelect.innerHTML = [
    `<option value="">${escapeHtml(t('buildings.allBuildings'))}</option>`,
    ...buildingsCache.map(b =>
      `<option value="${b.id}"${b.id === equipmentFilters.building ? ' selected' : ''}>${escapeHtml(b.name)}</option>`
    ),
  ].join('')

  zoneSelect.innerHTML = [
    `<option value="">${escapeHtml(t('zones.allZones'))}</option>`,
    ...zonesCache
      .filter(z => z.building_id === equipmentFilters.building)
      .map(z =>
        `<option value="${z.id}"${z.id === equipmentFilters.zone ? ' selected' : ''}>${escapeHtml(z.name)}</option>`
      ),
  ].join('')

  zoneSelect.disabled = !equipmentFilters.building
}

// ==================== FILTERS MODAL ====================

function openFiltersModal() {
  const modal = document.getElementById('filtersModal')
  if (!modal) return

  // Buildings may not be loaded yet if the admin never opened that tab
  if (buildingsCache.length === 0) {
    loadBuildings().then(syncLocationFilterSelects)
  } else {
    syncLocationFilterSelects()
  }

  syncTypeButtons()
  modal.style.display = 'flex'
  refreshIcons()
}

function closeFiltersModal() {
  const modal = document.getElementById('filtersModal')
  if (modal) modal.style.display = 'none'
}

// Загружаем список клиентов в фильтр
function loadClientFilter() {
  const select = document.getElementById('filterClient')
  if (!select) return

  // Получаем уникальных клиентов
  const clients = [...new Set(allEquipment
    .map(item => item.client_company)
    .filter(Boolean)
  )].sort()

  // Сохраняем текущий выбор
  const currentValue = select.value

  // Очищаем и заполняем
  select.innerHTML = `<option value="" data-i18n="equipment.filterAllClients">All Clients</option>`

  clients.forEach(client => {
    const option = document.createElement('option')
    option.value = client
    option.textContent = client
    select.appendChild(option)
  })

  // Восстанавливаем выбор
  select.value = currentValue
}

// Загружаем список специалистов в фильтр
async function loadSpecialistFilter() {
  const select = document.getElementById('filterSpecialist')
  if (!select) return

  if (allSpecialists.length === 0) {
    try {
      const { data, error } = await supabase
        .from('users')
        .select('id, full_name')
        .eq('tenant_id', currentUser.tenant_id)
        .eq('role', 'mechanic')
        .eq('is_active', true)
        .order('full_name', { ascending: true })
      if (!error) allSpecialists = data || []
    } catch (e) {}
  }

  const currentValue = select.value
  select.innerHTML = `<option value="">All Specialists</option>`
  allSpecialists.forEach(s => {
    const option = document.createElement('option')
    option.value = s.id
    option.textContent = s.full_name
    select.appendChild(option)
  })
  select.value = currentValue
}

// Сбрасываем все фильтры
function clearAllFilters() {
  equipmentFilters = {
    search: '',
    client: '',
    status: '',
    type: '',
    sort: '',
    specialist: '',
    noNfc: false,
    building: '',
    zone: ''
  }

  // Сбрасываем UI
  const searchInput = document.getElementById('equipmentSearchInput')
  const clientSelect = document.getElementById('filterClient')
  const statusSelect = document.getElementById('filterStatus')
  const specialistSelect = document.getElementById('filterSpecialist')
  const sortSelect = document.getElementById('filterSort')
  const noNfcBtn = document.getElementById('filterNoNfcBtn')

  if (searchInput) searchInput.value = ''
  if (clientSelect) clientSelect.value = ''
  if (statusSelect) statusSelect.value = ''
  if (specialistSelect) specialistSelect.value = ''
  if (sortSelect) sortSelect.value = ''
  if (noNfcBtn) noNfcBtn.classList.remove('active')

  syncTypeButtons()
  syncLocationFilterSelects()

  renderFilteredEquipment()
  refreshIcons()
}

// Устанавливаем обработчики событий для фильтров
function setupFilterListeners() {
  // Поиск
  const searchInput = document.getElementById('equipmentSearchInput')
  if (searchInput) {
    searchInput.addEventListener('input', (e) => {
      equipmentFilters.search = e.target.value
      renderFilteredEquipment()
    })
  }
  
  // Клиент
  const clientSelect = document.getElementById('filterClient')
  if (clientSelect) {
    clientSelect.addEventListener('change', (e) => {
      equipmentFilters.client = e.target.value
      renderFilteredEquipment()
    })
  }
  
  // Статус
  const statusSelect = document.getElementById('filterStatus')
  if (statusSelect) {
    statusSelect.addEventListener('change', (e) => {
      equipmentFilters.status = e.target.value
      renderFilteredEquipment()
    })
  }
  
  // Тип оборудования + No NFC (единый обработчик)
  document.querySelectorAll('.type-filter-btn').forEach(btn => {
    btn.addEventListener('click', () => {
      document.querySelectorAll('.type-filter-btn').forEach(b => b.classList.remove('active'))
      btn.classList.add('active')

      if (btn.id === 'filterNoNfcBtn') {
        equipmentFilters.type = ''
        equipmentFilters.noNfc = true
      } else {
        equipmentFilters.type = btn.dataset.type
        equipmentFilters.noNfc = false
      }

      renderFilteredEquipment()
    })
  })

  // Специалист
  const specialistSelect = document.getElementById('filterSpecialist')
  if (specialistSelect) {
    specialistSelect.addEventListener('change', (e) => {
      equipmentFilters.specialist = e.target.value
      renderFilteredEquipment()
    })
  }

  // Сортировка
  const sortSelect = document.getElementById('filterSort')
  if (sortSelect) {
    sortSelect.addEventListener('change', (e) => {
      equipmentFilters.sort = e.target.value
      renderFilteredEquipment()
    })
  }

  // Здание — сбрасывает зону, потому что зона живёт внутри здания
  const buildingSelect = document.getElementById('filterBuilding')
  if (buildingSelect) {
    buildingSelect.addEventListener('change', (e) => {
      equipmentFilters.building = e.target.value
      equipmentFilters.zone = ''
      syncLocationFilterSelects()
      renderFilteredEquipment()
    })
  }

  // Зона
  const zoneSelect = document.getElementById('filterZone')
  if (zoneSelect) {
    zoneSelect.addEventListener('change', (e) => {
      equipmentFilters.zone = e.target.value
      renderFilteredEquipment()
    })
  }

  // Модалка фильтров
  document.getElementById('openFiltersBtn')?.addEventListener('click', openFiltersModal)
  document.getElementById('closeFiltersModal')?.addEventListener('click', closeFiltersModal)
  document.getElementById('applyFiltersBtn')?.addEventListener('click', closeFiltersModal)
  document.getElementById('filtersModal')?.querySelector('.modal-overlay')
    ?.addEventListener('click', closeFiltersModal)

  // Чипсы активных фильтров — снимаются кликом
  document.getElementById('filterChips')?.addEventListener('click', (e) => {
    const chip = e.target.closest('.filter-chip')
    if (chip) clearOneFilter(chip.dataset.filterKey)
  })

  // Сброс фильтров
  const clearBtn = document.getElementById('clearFiltersBtn')
  if (clearBtn) {
    clearBtn.addEventListener('click', clearAllFilters)
  }
  refreshIcons()
}

// Инициализируем фильтры при загрузке дашборда
setupFilterListeners()

window.addEventListener('languageChanged', async () => {
  await loadEquipmentList()
  if (currentEquipment) await openEquipmentDetail(currentEquipment.id)
  await loadMechanicsList()
  await loadOverdueCount()
  await loadServiceRequestsCount()
})
// ==================== RESET MECHANIC PASSWORD ====================

/**
 * Reset mechanic password - admin function
 */
window.resetMechanicPassword = async function(mechanicId, mechanicName, mechanicUsername) {
  if (!confirm(`Reset password for ${mechanicName}?\n\nA new temporary password will be generated.`)) {
    return
  }
  
  try {
    // Generate new temporary password
    const newPassword = generateStrongPassword(12)

    // Hash password with bcrypt before storing
    const { data: hashedNew, error: hashErr } = await supabase.rpc('hash_password', { plaintext: newPassword })
    if (hashErr) throw new Error('Failed to hash password: ' + hashErr.message)

    // Update user
    const { error } = await supabase
      .from('users')
      .update({
        password_hash: hashedNew,
        must_change_password: true
      })
      .eq('id', mechanicId)
    
    if (error) throw error
    
    // Show new password to admin
    const message = `✅ Password Reset Successful!\n\nMechanic: ${mechanicName}\nUsername: ${mechanicUsername}\nNew Password: ${newPassword}\n\n⚠️ Please save this and share with the mechanic.\nThey will be required to change it on next login.`
    
    alert(message)
    
    // Optional: Download as text file
    const download = confirm('Download credentials as text file?')
    if (download) {
      downloadPasswordFile(mechanicName, mechanicUsername, newPassword)
    }
    
  } catch (error) {
    console.error('Error resetting password:', error)
    alert('❌ Failed to reset password: ' + error.message)
  }
}

/**
 * Download password reset credentials as TXT file
 */
function downloadPasswordFile(mechanicName, mechanicUsername, password) {
  const content = `KIXIK - Password Reset
=====================

Mechanic: ${mechanicName}
Username: ${mechanicUsername}
New Password: ${password}

⚠️ Important:
 This is a temporary password
 Must be changed on next login
 Keep this information secure


Generated: ${new Date().toLocaleString()}
`
  
  const blob = new Blob([content], { type: 'text/plain' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `password-reset-${mechanicUsername}.txt`
  a.click()
  URL.revokeObjectURL(url)
}
// Показ/скрытие всей ИТ-секции в зависимости от главной категории
document.addEventListener('change', (e) => {
  if (e.target && e.target.id === 'equipmentType') {
    const itSection = document.getElementById('it_details_section');
    if (itSection) {
      // ID fc086b6e-8c5b-4cd0-9ea0-370f37eaf7d4 соответствует IT & Office Equipment
      itSection.style.display = (e.target.value === 'fc086b6e-8c5b-4cd0-9ea0-370f37eaf7d4') ? 'block' : 'none';
    }
  }
});

// Переключение полей внутри ИТ-секции (Ноутбук vs Принтер)
document.addEventListener('change', (e) => {
  if (e.target && e.target.id === 'it_subtype') {
    const val = e.target.value;
    const pcFields = document.getElementById('it_pc_fields');
    const netFields = document.getElementById('it_network_fields');
    
    if (pcFields) pcFields.style.display = (val === 'laptop') ? 'block' : 'none';
    if (netFields) netFields.style.display = (val === 'printer' || val === 'network') ? 'block' : 'none';
  }
});
// ==================== MOBILE SCROLL UX (FORCE MODE) ====================
document.addEventListener('click', (e) => {
  // 1. Ищем карточку (или её родителя)
  const card = e.target.closest('.stat-card');
  
  // Если клик не по карточке — выходим
  if (!card) return;

  // 2. Если это "Свободные теги", ничего не делаем (там модалка)
  if (card.id === 'tagsCard' || card.id === 'statTags') return;

  // 3. Проверка ширины экрана (только мобильные)
  if (window.innerWidth >= 1024) return;

  // 4. Ищем якорь для скролла (строка поиска)
  const searchInput = document.getElementById('searchInput');
  const filters = document.querySelector('.actions-bar');
  
  // Берем что нашлось (лучше поиск, если нет — панель фильтров)
  const target = searchInput || filters;

  if (target) {
    // Вычисляем позицию:
    // Текущий скролл + позиция элемента относительно верха окна - 100px (отступ для шапки)
    const yPosition = target.getBoundingClientRect().top + window.scrollY - 100;

    // Плавный скролл к точке
    window.scrollTo({
      top: yPosition,
      behavior: 'smooth'
    });
  } else {
    // План Б: если поиск не найден, просто скроллим на 400px вниз
    window.scrollBy({
      top: 400,
      behavior: 'smooth'
    });
  }
});
// ← ДОБАВИТЬ В КОНЕЦ ФАЙЛА
// ============================================
// AI SCAN - ADD EQUIPMENT MODAL (НОВЫЙ)
// ============================================
document.getElementById('scanWithAIBtn')?.addEventListener('click', () => {
    document.getElementById('aiPhotoInput').click()
})

// Обработка кнопки и инпута в модалке СОЗДАНИЯ
document.getElementById('scanWithAIBtn')?.addEventListener('click', () => {
  document.getElementById('aiPhotoInput')?.click()
})

// ==================== AI NAMEPLATE → CARD FIELDS ====================
// Base (statically rendered) inputs per mode. Schema fields not listed here
// fall through to the dynamic id pattern below.
const AI_BASE_INPUT_IDS = {
  add: {
    name: 'name',
    client_company: 'clientCompany',
    location: 'location'
  },
  edit: {
    name: 'editEquipmentName',
    client_company: 'editClientCompany',
    serial_number: 'editSerialNumber',
    location: 'editLocation'
  }
}

function aiTargetInput(mode, fieldId) {
  const baseId = AI_BASE_INPUT_IDS[mode]?.[fieldId]
  if (baseId) return document.getElementById(baseId)
  const dynId = mode === 'add' ? `field_${fieldId}` : `edit_field_${fieldId}`
  return document.getElementById(dynId)
}

// Collect the schema field list to send to the AI. Includes base text fields
// that aren't in fields_schema but exist statically in the form.
function aiCollectFields(mode) {
  const schema = selectedEquipmentType?.fields_schema || {}
  const out = []
  const seen = new Set()
  const push = (f) => {
    if (!f || !f.id || seen.has(f.id)) return
    seen.add(f.id)
    out.push({
      id: f.id,
      label: f.label || f.id,
      type: f.type || 'text',
      options: Array.isArray(f.options) ? f.options : undefined
    })
  }
  // Static fields not duplicated in schema
  push({ id: 'name', label: 'Equipment Name', type: 'text' })
  push({ id: 'client_company', label: 'Client Company', type: 'text' })
  push({ id: 'serial_number', label: 'Serial Number', type: 'text' })
  push({ id: 'location', label: 'Location', type: 'text' })
  ;(schema.passport || []).forEach(push)
  ;(schema.maintenance || []).forEach(push)
  return out
}

// Apply recognized fields to the form. Rules:
//  - skip date & select fields (user decides),
//  - only fill empty inputs (don't overwrite anything the user typed),
//  - number/text/textarea only.
function aiApplyFields(mode, fieldsObj, schemaFields) {
  if (!fieldsObj || typeof fieldsObj !== 'object') return 0
  const byId = new Map(schemaFields.map(f => [f.id, f]))
  let filled = 0
  for (const [fieldId, rawVal] of Object.entries(fieldsObj)) {
    if (rawVal === null || rawVal === undefined || rawVal === '') continue
    const schemaField = byId.get(fieldId)
    const type = schemaField?.type || 'text'
    if (type === 'date' || type === 'select') continue
    const input = aiTargetInput(mode, fieldId)
    if (!input) continue
    if (input.value && input.value.trim() !== '') continue
    const value = typeof rawVal === 'string' ? rawVal : String(rawVal)
    input.value = value
    input.dispatchEvent(new Event('input', { bubbles: true }))
    input.dispatchEvent(new Event('change', { bubbles: true }))
    filled++
  }
  return filled
}

async function aiHandleScan(mode, file) {
  const isEdit = mode === 'edit'
  const statusEl = document.getElementById(isEdit ? 'aiScanStatusEdit' : 'aiScanStatus')
  const textField = document.getElementById(isEdit ? 'edit_ai_scan_data' : 'add_ai_scan_data')
  const setStatus = (msg) => { if (statusEl) statusEl.textContent = msg }

  setStatus(t('equipment.aiScanning') || '🔄 Scanning...')
  try {
    const imageData = await new Promise((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = (ev) => resolve({
        base64: ev.target.result.split(',')[1],
        mediaType: ev.target.result.split(';')[0].split(':')[1]
      })
      reader.onerror = reject
      reader.readAsDataURL(file)
    })

    const schemaFields = aiCollectFields(mode)

    const res = await fetch(`${supabaseUrl}/functions/v1/ai-extract`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${supabaseKey}`, 'Content-Type': 'application/json', 'apikey': supabaseKey },
      body: JSON.stringify({ imageData, fields: schemaFields })
    })
    const data = await res.json()

    if (!data.success) throw new Error(data.error || 'AI request failed')

    if (textField) textField.value = data.text || ''

    const filled = aiApplyFields(mode, data.fields, schemaFields)
    if (filled > 0) {
      setStatus((t('equipment.aiScanFilled') || '✅ Filled {count} fields').replace('{count}', String(filled)))
    } else {
      setStatus(t('equipment.aiScanSuccess') || '✅ Done!')
    }
  } catch (err) {
    console.error('AI Error:', err)
    setStatus(t('equipment.aiScanError') || '❌ Error')
  }
}

document.getElementById('aiPhotoInput')?.addEventListener('change', async (e) => {
  const file = e.target.files[0]
  if (!file) return
  await aiHandleScan('add', file)
  e.target.value = ''
})

document.getElementById('scanWithAIEditBtn')?.addEventListener('click', () => {
  document.getElementById('aiPhotoInputEdit')?.click()
})

document.getElementById('aiPhotoInputEdit')?.addEventListener('change', async (e) => {
  const file = e.target.files[0]
  if (!file) return
  await aiHandleScan('edit', file)
  e.target.value = ''
})

// ==================== GLOBAL HANDLERS (FIX) ====================
// Специальная глобальная функция для клика по просрочке
window.handleOverdueClick = function(equipmentId) {
  console.log('Overdue item clicked:', equipmentId);
  
  // 1. Принудительно закрываем модалку
  const modal = document.getElementById('overdueModal');
  if (modal) modal.style.display = 'none';
  
  // 2. Открываем детали (с небольшой задержкой для глазу)
  setTimeout(() => {
    if (window.openEquipmentDetail) {
      window.openEquipmentDetail(equipmentId);
    } else {
      console.error('openEquipmentDetail function not found!');
    }
  }, 100);
};

// Убедимся, что openEquipmentDetail тоже глобальна (если ещё нет)
// Найди, где у тебя объявлена openEquipmentDetail и убедись, что в конце файла есть:
window.openEquipmentDetail = openEquipmentDetail;
// Логика кнопки Clear в модалке Создания
document.getElementById('clearAIScanAddBtn')?.addEventListener('click', () => {
    const textArea = document.getElementById('add_ai_scan_data');
    if (textArea) textArea.value = '';
    document.getElementById('aiScanStatus').textContent = '';
});

// Логика кнопки Clear в модалке Редактирования
document.getElementById('clearAIScanEditBtn')?.addEventListener('click', () => {
    const textArea = document.getElementById('edit_ai_scan_data');
    if (textArea) textArea.value = '';
    document.getElementById('aiScanStatusEdit').textContent = '';
});
async function uploadPhoto(file, equipmentId) {
  if (!file) return null;
  
  // Сжимаем
  const compressedBlob = await compressImage(file);
  const fileName = `${equipmentId}/${Date.now()}.jpg`;

  const { data, error } = await supabase.storage
    .from('equipment-photos')
    .upload(fileName, compressedBlob, {
      contentType: 'image/jpeg',
      upsert: true
    });

  if (error) throw error;

  // Получаем публичную ссылку
  const { data: { publicUrl } } = supabase.storage
    .from('equipment-photos')
    .getPublicUrl(fileName);

  return publicUrl;
}
// ==================== PHOTO PREVIEW HANDLERS ====================

// Функция для настройки превью
// Переменная для отслеживания удаления фото в редактировании
let isPhotoRemoved = false;

function setupPhotoPreview(inputId, previewId, placeholderId, removeBtnId) {
  const input = document.getElementById(inputId);
  const preview = document.getElementById(previewId);
  const placeholder = document.getElementById(placeholderId);
  const removeBtn = document.getElementById(removeBtnId);

  if (!input || !preview) return;

  // Обработка выбора файла
  input.addEventListener('change', function(e) {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = function(e) {
        preview.src = e.target.result;
        preview.style.display = 'block';
        if (placeholder) placeholder.style.display = 'none';
        if (removeBtn) removeBtn.style.display = 'flex';
        isPhotoRemoved = false; // Если выбрали новый файл, сбрасываем флаг удаления
      }
      reader.readAsDataURL(file);
    }
  });

  // Обработка удаления
  if (removeBtn) {
    removeBtn.addEventListener('click', (e) => {
      e.preventDefault();
      e.stopPropagation(); // Чтобы не сработал клик по инпуту
      
      input.value = ''; // Очищаем инпут
      preview.src = '';
      preview.style.display = 'none';
      if (placeholder) placeholder.style.display = 'flex';
      removeBtn.style.display = 'none';
      
      // Если это модалка редактирования, ставим флаг удаления
      if (inputId === 'editEquipmentPhotoInput') {
        isPhotoRemoved = true;
      }
    });
  }
}

// ==================== KEYBOARD SHORTCUTS ====================
document.addEventListener('keydown', (e) => {
  if (e.key !== 'Escape') return
  const modals = [
    { el: document.getElementById('checklistModal'),    close: () => closeChecklistModal() },
    { el: editEquipmentModal,                           close: closeEditModalFn },
    { el: addEquipmentModal,                            close: closeEquipmentModalFn },
    { el: equipmentDetailModal,                         close: closeDetailModalFn },
    { el: document.getElementById('addMechanicModal'),  close: closeMechanicModalFn },
    { el: document.getElementById('mechanicCredentialsModal'), close: closeCredentialsModalFn },
  ]
  for (const { el, close } of modals) {
    if (el && el.style.display !== 'none') { close(); break }
  }
})

// Инициализация при загрузке страницы
document.addEventListener('DOMContentLoaded', () => {
  setupPhotoPreview('equipmentPhotoInput', 'photoPreview', 'photoPlaceholderAdd', 'removePhotoBtnAdd');
  setupPhotoPreview('editEquipmentPhotoInput', 'photoPreviewEdit', 'photoPlaceholderEdit', 'removePhotoBtnEdit');
});

// ==================== EQUIPMENT TAG SCANNER ====================
// Позволяет админу открыть карточку оборудования, отсканировав QR-код метки.
let equipmentScanner = null

async function stopEquipmentScanner() {
  if (equipmentScanner) {
    try { await equipmentScanner.stop() } catch { /* ignore */ }
    try { equipmentScanner.clear() } catch { /* ignore */ }
    equipmentScanner = null
  }
}

function closeScanModal() {
  const modal = document.getElementById('scanEquipmentModal')
  if (modal) modal.style.display = 'none'
  const select = document.getElementById('scanCameraSelect')
  if (select) { select.style.display = 'none'; select.innerHTML = '' }
  stopEquipmentScanner()
}

// Резолвим отсканированный код (полный URL .../e/CODE или просто CODE) в оборудование
async function openEquipmentByScannedCode(decodedText) {
  const raw = (decodedText || '').trim()
  const code = raw.includes('/e/') ? raw.split('/e/')[1].split(/[/?#]/)[0] : raw
  if (!code) return

  const { data: tag, error } = await supabase
    .from('nfc_tags')
    .select('equipment_id, tenant_id')
    .eq('url_code', code)
    .eq('tenant_id', currentUser.tenant_id)
    .maybeSingle()

  if (error || !tag || !tag.equipment_id) {
    alert(t('equipment.scanNotFound') || 'Equipment for this tag was not found.')
    return
  }

  closeScanModal()
  if (window.openEquipmentDetail) window.openEquipmentDetail(tag.equipment_id)
}

async function startEquipmentScanner() {
  const modal = document.getElementById('scanEquipmentModal')
  if (!modal) return

  if (!window.Html5Qrcode) {
    alert(t('equipment.scannerUnavailable') || 'Scanner could not load. Please check your connection.')
    return
  }

  modal.style.display = 'flex'
  await stopEquipmentScanner()
  equipmentScanner = new Html5Qrcode('equipment-qr-reader')

  let isProcessing = false
  const onSuccess = (decodedText) => {
    if (isProcessing) return
    isProcessing = true
    openEquipmentByScannedCode(decodedText)
  }

  const config = { fps: 10, qrbox: { width: 250, height: 250 } }

  const startWithCamera = async (cameraId) => {
    if (!equipmentScanner) return
    try { await equipmentScanner.stop() } catch { /* not running yet */ }
    await equipmentScanner.start(cameraId, config, onSuccess)
  }

  try {
    const cameras = await Html5Qrcode.getCameras()
    if (!cameras || cameras.length === 0) throw new Error('No cameras found')

    const select = document.getElementById('scanCameraSelect')
    if (cameras.length > 1 && select) {
      // На десктопе с несколькими камерами/USB-сканером даём выбрать вручную.
      select.innerHTML = cameras
        .map((c, i) => `<option value="${c.id}">${escapeHtml(c.label || `Camera ${i + 1}`)}</option>`)
        .join('')
      select.style.display = 'block'
      select.onchange = () => startWithCamera(select.value)
    } else if (select) {
      select.style.display = 'none'
    }

    // По умолчанию последняя камера (на телефоне обычно задняя).
    await startWithCamera(cameras[cameras.length - 1].id)
  } catch (err) {
    console.error('Scanner start error:', err)
    alert(t('error.cameraAccessDenied') || 'Camera error')
    closeScanModal()
  }
}

document.getElementById('scanEquipmentBtn')?.addEventListener('click', startEquipmentScanner)
document.getElementById('closeScanModalBtn')?.addEventListener('click', closeScanModal)
document.getElementById('scanEquipmentModal')?.querySelector('.modal-overlay')?.addEventListener('click', closeScanModal)
